// Station to station: the trip planner PTV does not publish.
//
// PTV has no journey-planning endpoint. It has two things that add up to one:
// the bundled network in `network.json` — 225 stations, every line's ordered
// stops, the through-running pairs — and `/v3/pattern/run/{ref}`, which turns
// the `run_ref` on a departure into every stop THAT TRAIN makes, with a time
// against each. So the shape of the network says which trains could be useful,
// and the run pattern says what they actually do.
//
// THE PATTERN IS THE GROUND TRUTH, and nothing else in this file is allowed to
// contradict it. The bundled stop lists carry PTV's own artifacts — Pakenham
// and Cranbourne are listed as calling at Parliament though a tunnel train
// never goes there, Frankston lists Burnley — and they know nothing about the
// express that will run straight through the station somebody is standing at.
// Every leg is therefore proposed from the graph and CONFIRMED from a pattern:
// if the boarding stop and the alighting stop are not both on the run, in that
// order, the train is discarded and the plan says nothing about it. An honest
// empty answer is the only acceptable failure here; an invented one puts a
// person on a platform for a train that will not stop.
//
// Pure, with `io` injected. No Vue, no store, no fetch: everything that talks
// to the network is passed in as `io = { departures(stopId, routeIds), run(ref) }`,
// which is what makes the whole of the planning logic testable against a fake
// with a call counter — and the call count is a real constraint, not a tidiness
// one. PTV asks for reasonable use and this app is opened at a barrier with one
// thumb, so a plan is capped: a handful of signed calls in about three round
// trips for a normal journey, and a wider cap that only the two-change fallback
// can reach, because it is only ever reached after a plan has already failed.

import {
  lineStops, throughPairs, isCityEnd, stationById, knownRouteIds, lineName, lineRecord, walkPartner,
  formatDistance, WALK_MINUTES, vlineBarred,
} from './network.js';
import { currentCity, city as cityInfo } from './city.js';
import { zoneSpan } from './fares.js';
import {
  directionOf, normaliseStationName, oneRowPerTrain, stationLineIds,
} from './departures.js';
import {
  SPECIAL_CANDIDATES, offLineStops, probeRoutes, specialRows,
} from './specials.js';
import { prefs } from './core/storage.js';
import { isBusy } from './busy.js';
import { modeOfRide } from './modes.js';
/** One best option, big, and two more behind an affordance. */
export const MAX_OPTIONS = 5;
/**
 * Options a TRIP PLAN offers, which is fewer than the ceiling above.
 *
 * THREE, BECAUSE THE FOURTH AND FIFTH ARE BOUGHT WITH THE ANSWER. Every option
 * past the first is a board row confirmed by its own pattern and, on a journey
 * with a change in it, a connection confirmed by another — call it four signed
 * calls each on a cold cache. Carnegie to Southern Cross, measured live,
 * reached the fourth and fifth by asking a SECOND change station about trains
 * twenty minutes behind the ones already on the card, and the whole plan took
 * fifty-seven seconds because of it. Three trains out of Carnegie is half an
 * hour of railway and it is more than anybody standing at a barrier is
 * choosing between.
 *
 * `MAX_OPTIONS` is untouched: the reroute engine spreads its own budget across
 * several origins and asks for five deliberately, and nothing about this
 * number is a claim about that one.
 */
export const PLAN_OPTIONS = 3;
/**
 * How long a plan may keep somebody waiting, whatever else is true.
 *
 * TWELVE SECONDS, AND THIS IS THE FLOOR UNDER THE WHOLE FILE. Everything above
 * is an argument about spending fewer calls; this is the promise that holds
 * when the argument is wrong. A plan is one screen with one loader on it, and
 * a loader that is still turning after twelve seconds is not working, it is
 * lying — the person has already decided the app is broken, and they are right
 * to. So at twelve seconds the plan stops asking for anything and answers with
 * what it has: three options, or one, or none and a sentence that says the
 * plan ran out of time rather than the network being down.
 *
 * It is not a limit on the network. The calls already sent finish into their
 * caches and the next plan is faster for it. It is a limit on the silence.
 */
export const PLAN_DEADLINE_MS = 20_000;
/**
 * When a plan stops looking for a BETTER answer, having found a real one.
 *
 * TEN SECONDS, and it is a different question from the deadline above. That
 * one is about a plan with nothing to show; this one is about a plan that
 * already has a train on it and is spending the next ten seconds on the train
 * after that. Measured on the deployed service: the best option for Carnegie
 * to Southern Cross lands about fifteen seconds in on a cold cache, and the
 * second and third cost four more seconds each — four seconds of loader, for a
 * journey ten minutes later than the one already found.
 *
 * So once there is an option in hand and the clock has passed this, the plan
 * stops and offers it. Nothing is lost that a person standing at a barrier was
 * going to read: they are deciding whether to walk to the platform, not
 * comparing itineraries. A plan that answers inside the ten seconds is
 * untouched and still offers three.
 */
export const PLAN_ENOUGH_MS = 10_000;
/**
 * Signed PTV calls one plan may spend.
 *
 * SIXTEEN, BECAUSE THE GATE ALLOWS SIXTEEN A MINUTE. It was twenty-eight, and
 * twenty-eight was a number with nothing on the other side of it: `OPERATORS`
 * in `server/gate.mjs` lets this app make sixteen PTV calls a minute with a
 * burst of four, so a plan permitted to want twenty-eight was a plan that
 * COULD NOT FINISH inside a minute on a completely idle server. It did not
 * degrade either — it failed, because the refusal rejected — so the one person
 * using the app got "Couldn't reach PTV" from a plan that was allowed to ask
 * for more than the app is allowed to spend.
 *
 * Measured rather than guessed: Carnegie to Richmond at 23:10, cold cache,
 * costs eighteen calls and sixty-four seconds if every one of them queues.
 * Nobody waits sixty-four seconds. Sixteen is the sustained minute, and the
 * plan now stops there and offers what it has, which for that trip is the
 * first three trains and the change at Caulfield. The two things that pay for
 * it are `settle` in `callGate`, which turns a busy call into a shorter
 * plan rather than an error, and the ordering in `time`, which spends the
 * budget on the most direct itinerary first.
 */
export const CALL_BUDGET = 16;
/**
 * Departures examined at the boarding station, and at a city one.
 *
 * THREE, NOT FIVE, AND THE REASON IS ARITHMETIC RATHER THAN TASTE. Every
 * candidate is a pattern call, and since the Metro Tunnel opened most of them
 * are TWO: a Cranbourne train out of Carnegie continues as a Sunbury run, so
 * its own pattern does not reach Richmond and the continuation has to be read
 * as well. Five candidates is therefore ten calls before a single option
 * exists, against a gate that allows sixteen a minute for the whole app. That
 * is how a plan came to cost eighteen calls and sixty-four seconds, measured
 * live, and how it came to fail outright when a board had spent anything at
 * all beforehand.
 *
 * Three candidates on a metropolitan line is half an hour of trains, and the
 * fourth and fifth were mostly the same journey ten and twenty minutes later
 * — the ranking put them below the first three anyway. What is bought with the
 * four calls saved is that the plan finishes at all.
 *
 * A CITY BOARD KEEPS ONE MORE. Flinders Street is eight lines on one board, so
 * the rows are not successive trains on one route the way they are at
 * Carnegie; four is the smallest number that still sees a second line.
 */
export const CANDIDATES = 3;
export const CITY_CANDIDATES = 4;
/**
 * Departures examined on a V/Line board, which is fewer than anywhere else.
 *
 * A metropolitan line runs every ten minutes, so five candidates at Carnegie
 * are an hour of trains and the fifth is worth a call. Geelong is hourly and
 * Bairnsdale is four times a day: the fifth row on a regional board can be
 * four hours out, and its pattern is a call spent reading the stopping pattern
 * of a train nobody standing there is going to catch. Three is the whole
 * morning on most of these lines, and the calls it saves are what pays for
 * thirteen more corridors inside the same `CALL_BUDGET`.
 */
export const REGIONAL_CANDIDATES = 3;

/**
 * What a regional leg is worth, in stations of metropolitan railway.
 *
 * THE RANKING IS IN HOPS AND A HOP IS NOT A MINUTE. A V/Line train skips most
 * of the stations it passes, so counted in stations it beats the metropolitan
 * line running beside it every time — Flinders Street to Carnegie is three
 * stations on a Gippsland service and six on the tunnel, and the Gippsland
 * service is hourly. Offering it would be the planner sending somebody to wait
 * fifty minutes to save three stops, and it also moves them onto a leg whose
 * fare may not be a myki fare at all.
 *
 * So a regional leg carries ten stations of debt: more than any metropolitan
 * detour round the city costs, and small enough that a journey which can ONLY
 * be made on V/Line — Carnegie to Lara, Ballarat to Sunbury — is unaffected,
 * because every proposal for it pays the same debt. That is the whole of the
 * old exclusion's good sense, kept, without its refusal.
 */
export const REGIONAL_HOPS = 10;

// ── How long a change takes ──────────────────────────────────────────────────
//
// This used to be one minute, everywhere, and it was the most confident wrong
// number in the app. A minute is not a change; it is the gap between a train
// arriving and a train leaving, which is a thing a timetable records and not a
// thing a person does. "There doesn't seem to be much time to change trains in
// the plans" is the complaint, and the complaint is right: every plan read
// CHANGE AT FOOTSCRAY · 1 MIN, and anybody who has carried a bag over the
// Footscray overpass knows that is not an offer, it is a dare.
//
// So the buffer is a QUESTION now rather than a constant, and it is asked of
// three things in turn:
//
//   1. THE STATION. A cross-platform change at Caulfield is genuinely three
//      minutes and pretending otherwise would throw away real connections;
//      Southern Cross is six, because the V/Line platforms are at the other
//      end of the longest concourse in Victoria. The table is the city's, in
//      `city.js`, keyed by station NAME.
//   2. THE TRAIN BEING BOARDED. A missed metropolitan train costs ten minutes
//      and a missed Geelong train costs an hour, so a regional leg gets a
//      floor of five minutes wherever it is boarded — including at stations
//      the table calls ordinary.
//   3. THE PERSON. Some people run for trains and some people do not, and no
//      table knows which. `changeMargin` is nought, two or five minutes on top
//      of everything above, chosen in Settings.
//
// A WALK IS ALREADY A CHANGE. Where the two halves of an interchange are
// joined by a footway — State Library to Melbourne Central — the walk carries
// its own `WALK_MINUTES` and that IS the time on foot; adding a station's
// change minutes to it would charge a person twice for the same subway and
// would quietly stop offering the walked connection that `bestInterchanges`
// went to the trouble of proposing. So the station's minutes are not added
// there; only the person's own margin is.

/** The change a city's table falls back to, and what an unlisted station gets. */
export const CHANGE_DEFAULT_MINUTES = 3;

/**
 * The least any change onto a V/Line train may be, whatever the station says.
 *
 * Five rather than the table's three or four because the cost of being wrong
 * is not symmetrical. The next tunnel train is ten minutes behind; the next
 * Geelong train is an hour behind, and on Bairnsdale it is the afternoon.
 */
export const REGIONAL_CHANGE_MINUTES = 5;

/** The margins a person may choose, in minutes. Nought is the default. */
export const CHANGE_MARGINS = [0, 2, 5];

/**
 * The pref the margin lives under — the key `prefs` reads, spelled once.
 *
 * `app-state.js` imports this rather than spelling it again, so the ref the
 * Settings screen binds and the value the planner reads cannot drift apart.
 */
export const CHANGE_MARGIN_KEY = 'lateagain:change-margin:v1';

/** The stored margin, or nought — which is also what a bad stored value means. */
export function changeMargin() {
  const n = Number(prefs[CHANGE_MARGIN_KEY]);
  return CHANGE_MARGINS.includes(n) ? n : 0;
}

let CHANGE_TABLE = null;
let CHANGE_TABLE_CITY = null;

/**
 * The city's change table with its station names normalised, memoised.
 *
 * Memoised against the CITY for the same reason `corridors()` is: a Melbourne
 * table answering Sydney questions would be a planner quietly using Footscray's
 * four minutes at Strathfield.
 */
function changeTable() {
  const id = currentCity();
  if (CHANGE_TABLE && CHANGE_TABLE_CITY === id) return CHANGE_TABLE;
  const table = cityInfo().change ?? {};
  CHANGE_TABLE = {
    default: Number(table.default) || CHANGE_DEFAULT_MINUTES,
    stations: new Map(Object.entries(table.stations ?? {})
      .map(([name, minutes]) => [normaliseStationName(name), Number(minutes)])
      .filter(([, minutes]) => Number.isFinite(minutes))),
  };
  CHANGE_TABLE_CITY = id;
  return CHANGE_TABLE;
}

/**
 * The change an ORDINARY station gets, which is the number Settings shows.
 *
 * The chip reads what a person will see on their plans — three, five or eight
 * minutes — rather than the margin that produced it, because "2" is not an
 * answer to "how long do I get to change trains". The city's own default is
 * what it adds to, so a city whose table moves moves this with it.
 */
export function typicalChangeMinutes(margin = changeMargin()) {
  return changeTable().default + Math.max(0, Number(margin) || 0);
}

/**
 * The least time this change may be given, in minutes.
 *
 * `station` is the station changed at — the record, its name, or its stop id,
 * because the callers hold different things and none of them should have to
 * look the others up. `nextLeg` is the leg being BOARDED, which is what the
 * regional floor is about: it is the train you would miss.
 *
 * Pure, given `margin`. It reads the stored margin when nothing is passed,
 * which is how the planner calls it and how a test pins the default.
 */
export function changeMinutes(station, nextLeg, { walked = false, margin = changeMargin() } = {}) {
  const extra = Math.max(0, Number.isFinite(Number(margin)) ? Number(margin) : 0);
  // The walk IS the change. See the note above.
  if (walked) return extra;

  const table = changeTable();
  const name = typeof station === 'number' || (typeof station === 'string' && /^\d+$/.test(station))
    ? stationById(Number(station))?.name
    : (typeof station === 'string' ? station : station?.name);
  const base = table.stations.get(normaliseStationName(name ?? '')) ?? table.default;
  const floor = isRegionalLeg(nextLeg) ? REGIONAL_CHANGE_MINUTES : 0;
  return Math.max(base, floor) + extra;
}

/**
 * How deep a board to ask for when the ordinary one does not reach the change.
 *
 * SIXTEEN ROWS, which is PTV's cap, and it is `max_results` per route and
 * direction rather than a total. The ordinary board is eight — eighty minutes
 * of a ten-minute line, less of a busier one — and every metropolitan change
 * in this app happens well inside that. A V/Line leg does not: Lara to
 * Footscray is ninety-two minutes, so the tunnel train a person changes onto
 * is off the end of a board fetched when the plan was made.
 *
 * WHEN it is asked for is decided in `connectionsFor` and is decided by the
 * board itself: a deeper board is a second signed call at the same platform,
 * so it is spent only where the board in hand has no row late enough to be the
 * connection. The clock used to decide it, and a clock cannot tell a Geelong
 * train from the third Frankston train of the morning.
 */
export const DEEP_BOARD_ROWS = 16;
/**
 * What an unasked board is worth, in rows, for comparing two of them.
 *
 * `max_results` on PTV's departures call, which the server passes through and
 * defaults to eight. It is here so that "is the board already in hand deeper
 * than the one being asked for" is a question with an answer when one of them
 * named no depth at all.
 */
export const DEFAULT_BOARD_ROWS = 8;
const depth = (rows) => (Number(rows) > 0 ? Number(rows) : DEFAULT_BOARD_ROWS);
/**
 * Connections examined at the change station.
 *
 * THREE LOOKED AT, ONE USUALLY PAID FOR. This is the number of board rows the
 * search is willing to consider, not the number of patterns it fetches: the
 * patterns are read one at a time and the first connection that works ends the
 * search, which on an ordinary change is the first row. Fetching all three up
 * front cost three calls for an answer the first one gave, and under a gate
 * that meters this app to one call every three and three quarter seconds,
 * three parallel calls are not faster than one — they are the same wait, three
 * times over, and two of the three are thrown away.
 */
export const CONNECTIONS = 3;
/** Places to get off one run, ranked, before the search gives up on it. */
export const CHANGE_POINTS = 3;

// Two changes are the fallback and are planned on a thinner budget than one:
// they are only ever reached when the one-change plans came back with nothing,
// so whatever is spent on them is spent AFTER a plan has already failed, and
// the person is still standing at the barrier. Two trains off the first board,
// two connections at each change, and the search stops at the first journey
// that works rather than comparing three of them.
/** Itineraries the graph keeps when it has to propose two changes. */
export const TWO_CHANGE_ITINERARIES = 3;
/** Departures examined at the boarding station for a two-change journey. */
export const TWO_CHANGE_CANDIDATES = 2;
/** Connections examined at each of its two changes. */
export const TWO_CHANGE_CONNECTIONS = 2;
/**
 * Two-change options offered, and one per itinerary.
 *
 * A fallback does not need a shortlist. The second train out of Carnegie on
 * the same three-train route is the same journey ten minutes later, and the
 * calls it costs are the calls the NEXT itinerary needs to find a different
 * and possibly much faster way round — which is the only thing worth having
 * once a journey this long is on the screen.
 */
export const TWO_CHANGE_OPTIONS = 2;
/**
 * Places to get off the MIDDLE train, which is more than anywhere else gets.
 *
 * Because the last line's city platforms are a loop and the hop count between
 * them is loop order, not distance. Southern Cross is fifteen stations from
 * Merlynston down the Upfield list and Parliament is twelve — and after nine at
 * night Southern Cross is the only one of the five with an Upfield train on it.
 * Three tries would rank the three dead platforms above the live one. They cost
 * almost nothing to rule out: the boards at the dead ones were fetched while
 * the one-change plan was failing and are still in hand.
 */
export const TWO_CHANGE_POINTS = 5;

/**
 * The last resort: one more train than the two-change fallback allows.
 *
 * Carnegie to Union after nine at night is the case. The Belgrave and Lilydale
 * lines stop running through the city, so a city train ends at Camberwell and a
 * separate service runs on to Union: the tunnel to Town Hall, a walk, a
 * Sandringham train to Richmond, a Belgrave-line train to Camberwell and
 * another to Union. Four trains and three changes, and every one of them is a
 * real train. Before this the plan reported that it could not join the two
 * stations, which is a false answer of the kind this file exists to avoid.
 *
 * It costs what it costs, so it runs only when everything shorter came back
 * empty, and it is given room to: `THREE_CHANGE_CALLS` more calls than the plan
 * was allowed, and until `THREE_CHANGE_DEADLINE_MS` from the start. A plan that
 * has nothing to show is not helped by an early answer of nothing.
 */
export const THREE_CHANGE_CALLS = 6;
/** Calls the two-change stage may spend before the last resort gets its turn. */
export const TWO_CHANGE_STAGE_CALLS = 4;
export const THREE_CHANGE_DEADLINE_MS = 60_000;

// ── The graph ────────────────────────────────────────────────────────────────

/**
 * Every through-running pair, once.
 *
 * `throughPairs()` lists both directions because the strip is drawn from either
 * end, but a corridor is built ONCE per pair — building it from both ends would
 * produce the same run of stations twice under two names. This used to be the
 * hand-written list `[4, 11, 12]`, "the suburban side", which was a third place
 * to keep the Melbourne network in step; it is derived now, so a city whose
 * table is empty — Sydney's is, and correctly — simply yields nothing.
 *
 * Since the Metro Tunnel opened, a Pakenham train at Carnegie is signed SUNBURY
 * and keeps going to Sunbury; the journey from Carnegie to Footscray is one
 * train, and a planner that stops thinking at the city sends somebody to change
 * at a station the train runs straight through.
 */
function throughPairsOnce() {
  const table = throughPairs();
  const seen = new Set();
  const out = [];
  for (const [from, tos] of Object.entries(table)) {
    for (const to of tos ?? []) {
      const a = Number(from);
      const b = Number(to);
      const key = a < b ? `${a}+${b}` : `${b}+${a}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // The lower id is called the suburban side. Which of the two it is does
      // not matter — the corridor is the same stations either way round — only
      // that the choice is the same on every rebuild, so a corridor id is
      // stable and a leg that stored one still resolves.
      out.push(a < b ? [a, b] : [b, a]);
    }
  }
  return out;
}

/**
 * The lines the planner will put somebody on — EVERY line the bundle knows.
 *
 * This used to exclude V/Line, and it was the one place in the app that did.
 * The exclusion made the planner answer "no way there by train" for Carnegie
 * to Lara, which is a trip a person makes with one change at Footscray, and
 * "no way there by train" about a railway that exists is the worst answer this
 * app can give. So regional lines are corridors like any other now, and the
 * three objections the old comment raised are each answered somewhere the
 * reader can go and look at:
 *
 * 1. A V/LINE SEAT IS NOT ALWAYS A MYKI FARE. `zoneSpan` in fares.js is what
 *    decides, and it refuses to price a journey that touches a station the
 *    bundle gives no zone at all (Wangaratta, Warrnambool) or a zone past the
 *    myki boundary (Lara is `[2, 3, 4]`). The Trip card then draws no fare
 *    line and says "V/Line fare, bought separately" instead of a number that
 *    would be wrong.
 * 2. SOME V/LINE SERVICES REQUIRE A RESERVATION. `RESERVED_ROUTES` below names
 *    the five long-distance lines, and an option with a leg on one of them
 *    carries "Reserved seating on this service". A person is told before they
 *    walk to the platform rather than turned away at it.
 * 3. THE SEARCH IS WIDER AND THE BUDGET IS NOT. Thirteen more corridors take
 *    the graph from fifteen to twenty-eight, and `CALL_BUDGET` is unchanged at
 *    twenty-eight PTV calls. What pays for it is `REGIONAL_CANDIDATES`: a
 *    V/Line board is an hourly service, so the fifth row on it is four hours
 *    away and its pattern is a call spent on a train nobody is catching.
 *
 * The ONE thing the exclusion did right — never proposing a regional leg where
 * a metropolitan one is plainly better — falls out of the ranking rather than
 * out of a filter: Carnegie to Caulfield is still direct, because `plan()`
 * takes a direct leg over every change there is. Pinned in
 * `tests/journey-graph.test.js`.
 */
function plannableRouteIds() {
  return knownRouteIds().sort((a, b) => a - b);
}

/**
 * The V/Line lines whose seats are booked, not boarded.
 *
 * Long-distance services: Warrnambool, Bairnsdale, Swan Hill, Shepparton and
 * Albury. PTV flags reservation per stop (`vline_reservation`) on the stop
 * facilities endpoint, which is a call this planner does not make and cannot
 * afford to make per option — so the fact lives with the city, in
 * `CITIES.melbourne.reserved`, as five route ids.
 *
 * It is used for a NOTE and never for a refusal. The commuter lines to
 * Geelong, Ballarat, Bendigo, Traralgon and Seymour are turn-up-and-go, and
 * the five here still run to a timetable somebody can read off this screen;
 * what they cannot do is walk onto one without a booked seat, which is the
 * sentence the card says.
 */
export function reservedRoutes() {
  return new Set((cityInfo().reserved ?? []).map(Number));
}

/**
 * Whether this leg rides a V/Line line. Walks never do.
 *
 * TWO READINGS OF ONE LEG, because a leg means two different things either
 * side of `time()`. A leg with a `runRef` on it has a real train, and the
 * train's own route is the answer — the Geelong service, not the Warrnambool
 * route the same board row also appears under. A leg the graph proposed
 * carries every route that could carry it, and it is regional only when there
 * is no metropolitan train among them: Footscray to Southern Cross lists
 * Werribee, Williamstown, Geelong and Warrnambool, and a Werribee train is
 * there every ten minutes, so that leg is not a V/Line leg and must not be
 * priced or noted as one.
 */
export function isRegionalLeg(leg) {
  if (!leg || leg.kind === 'walk') return false;
  if (leg.runRef) return Boolean(lineRecord(Number(leg.routeId))?.regional);
  const ids = (leg.routeIds?.length ? leg.routeIds : [leg.routeId]).map(Number).filter(Number.isFinite);
  return ids.length > 0 && ids.every((id) => lineRecord(id)?.regional);
}

/**
 * Whether any leg of this option is on a line you have to book a seat on.
 *
 * Read off the leg's OWN route id where a live train has set one — the run's
 * route, which `optionLeg` copies off the pattern — and off the planned
 * `routeIds` otherwise. The distinction is the whole accuracy of the line: a
 * proposed Footscray-to-Lara leg lists Geelong AND Warrnambool, and only one
 * of those is a train you have to book. Once a train is on the leg, it is that
 * train that is asked.
 */
export function needsReservation(option) {
  const reserved = reservedRoutes();
  return (option?.legs ?? []).some((leg) => {
    if (leg?.kind === 'walk') return false;
    if (leg.runRef) return reserved.has(Number(leg.routeId));
    // Untimed, so EVERY line that could carry it has to be one you book —
    // otherwise the sentence is about a train that may never be the one this
    // journey puts you on. Footscray to Southern Cross lists Warrnambool among
    // four others and a Werribee train is there every ten minutes.
    const ids = (leg.routeIds?.length ? leg.routeIds : [leg.routeId]).map(Number).filter(Number.isFinite);
    return ids.length > 0 && ids.every((id) => reserved.has(id));
  });
}

/** Two sentences, and the only two this planner adds to an option. */
export const VLINE_FARE_NOTE = 'V/Line fare, bought separately';
export const RESERVATION_NOTE = 'Reserved seating on this service';

/**
 * The same fact outside Victoria. Perth's regional railcars are sold apart
 * from the suburban network, as V/Line is, but they are not V/Line — and they
 * are not named either, because the timetable's licence does not allow the
 * operator's brand alongside the data.
 */
export const REGIONAL_FARE_NOTE = 'Regional fare, bought separately';

/**
 * What has to be said about an option beyond its trains, as dim lines.
 *
 * Both are about V/Line and both are about the thing a timetable does not
 * tell you — what you are holding when you get on. The fare note appears when
 * this journey rides a regional line AND `zoneSpan` will not price it, which
 * is the same condition the card uses to draw no fare at all: the two go
 * together, so the row that would have carried a number carries the reason
 * instead. A regional leg that IS a myki trip — Southern Cross to Wyndham
 * Vale — gets the fare and no note, because there is nothing to buy
 * separately.
 *
 * Never more than these two, and never a note on a metropolitan journey.
 */
export function optionNotes(option) {
  const notes = [];
  const regional = (option?.legs ?? []).some((leg) => isRegionalLeg(leg));
  if (regional && !zoneSpan(option)) {
    notes.push(currentCity() === 'melbourne' ? VLINE_FARE_NOTE : REGIONAL_FARE_NOTE);
  }
  if (needsReservation(option)) notes.push(RESERVATION_NOTE);
  return notes;
}

/**
 * The two kinds of walk on a planned journey, told apart.
 *
 * `distanceBased` is a walk ACROSS THE MAP, from the last station to a place
 * that is not a station — the kilometre home, measured and offered with "or
 * get picked up". The other is the subway between two halves of one city
 * station: five minutes underground, inside the gates, and a change rather
 * than a journey. They read differently to a person, and every sentence about
 * walking in this app has to know which one it is talking about.
 */
const isPlaceWalk = (leg) => leg?.kind === 'walk' && Boolean(leg.distanceBased);
// Underground between two halves of one station: a walk with a mode's stop at
// either end (a tram's) is an overground walk, and not this.
const isInsideWalk = (leg) => leg?.kind === 'walk' && !leg.distanceBased
  && (!leg.fromKind || leg.fromKind === 'station') && (!leg.toKind || leg.toKind === 'station');

const COUNT_WORD = ['no', 'One', 'Two', 'Three', 'Four', 'Five'];
const countWord = (n) => COUNT_WORD[n] ?? String(n);

/**
 * What a journey asks of somebody on foot, in one or two sentences.
 *
 * IT USED TO CONTRADICT ITSELF IN PUBLIC. There are two kinds of walk and the
 * footnote counted only one of them, so a journey with a subway change AND a
 * kilometre home read "The last leg is on foot from Glen Huntly, about 19
 * minutes, or get picked up. The one walk is between two halves of the same
 * city station" — having just described the other one in the sentence before.
 * The same footnote promised "no trams or buses" on journeys whose middle leg
 * was a replacement bus.
 *
 * So it is BUILT rather than branched. Each walk that is really there
 * contributes its own clause, in the order somebody meets it, and the closing
 * claim about what else is on the journey is made only when it is true. Out
 * here rather than in the page because "is this sentence a lie" is exactly the
 * kind of question worth answering without a browser.
 */
export function walkFootnote(legs) {
  const rows = Array.isArray(legs) ? legs.filter(Boolean) : [];
  const nameOf = (stopId) => stationById(stopId)?.name ?? '';
  const bits = [];

  const first = isPlaceWalk(rows[0]) ? rows[0] : null;
  const tail = rows[rows.length - 1];
  const last = rows.length > 1 && isPlaceWalk(tail) ? tail : null;

  if (first) {
    const to = nameOf(first.toStopId);
    bits.push(`The first leg is on foot${to ? ` to ${to}` : ''}, about ${first.minutes} minutes, or get dropped off.`);
  }
  if (last) {
    const from = nameOf(last.fromStopId);
    bits.push(`The last leg is on foot${from ? ` from ${from}` : ''}, about ${last.minutes} minutes, or get picked up.`);
  }

  const inside = rows.filter(isInsideWalk).length;
  if (inside === 1) {
    bits.push('One change is made on foot, between two halves of the same city station, underground and inside the gates.');
  } else if (inside > 1) {
    bits.push(`${countWord(inside)} of the changes are made on foot, each between two halves of one city station, underground and inside the gates.`);
  }

  // A journey with a ride on a mode of its own (a tram) is not "trains only",
  // and must not say so; it says what is on foot and nothing about the rest.
  if (rows.some((leg) => modeOfRide(leg))) return bits.join(' ');

  const hasBus = rows.some((leg) => leg.kind === 'bus');
  if (!bits.length) {
    return hasBus
      ? 'No walking on this one. The replacement bus aside, it is trains the whole way.'
      : 'Trains only, no walking, trams or buses.';
  }
  // Only where it is true: a journey with a replacement bus on it has already
  // said so on the bus’s own row, and denying buses under it would be the
  // page arguing with itself again.
  if (!hasBus) bits.push('Everything else is on a train, with no trams or buses.');
  return bits.join(' ');
}

/**
 * An option’s headline: where you get off, and how far you walk from there.
 *
 * THE LINE LIST WAS NEVER THE HEADLINE. "Sunbury · Sandringham · Glen Waverley
 * lines" is three proper nouns and a plural in front of the single thing
 * somebody is choosing between, and the fact underneath it — "off at East
 * Malvern" — was the choice. On a screen whose whole subject is getting off
 * somewhere other than where you meant to, that is the headline, and the walk
 * it commits you to belongs in the same breath: East Malvern with 400 m after
 * it and East Malvern with 1.9 km after it are two different offers.
 */
export function offAtHeadline(option) {
  const to = option?.to?.name;
  if (!to) return '';
  const legs = Array.isArray(option?.legs) ? option.legs : [];
  const walk = legs.find((leg, i) => i > 0 && isPlaceWalk(leg));
  const metres = Number(walk?.metres);
  return Number.isFinite(metres) && metres > 0
    ? `Off at ${to}, ${formatDistance(metres)} on foot`
    : `Off at ${to}`;
}

let CORRIDORS = null;
let CORRIDORS_CITY = null;

/**
 * Every corridor a train can run end to end, memoised.
 *
 * Fourteen of them: sixteen lines, six of which (4, 11, 12, 14, 16, 17) merge
 * in pairs into the four through-running corridors, plus the ten that run to
 * the city and stop there. A corridor is `{ id, routeIds, stops, index, block,
 * hasCityEnd }` — `index` maps a stop id to its position, `block` is the range
 * of the city stations in the middle.
 */
export function corridors() {
  // Memoised against the CITY, not just against having been built. The graph is
  // the bundle turned inside out, so a corridor list carried across a switch
  // would be a Melbourne network answering Sydney questions — with stop ids
  // that exist in neither, since `stationById` would be looking in the other
  // bundle by then. Cheap to check, and the alternative is a `setCity` that has
  // to remember every memo in the codebase.
  const id = currentCity();
  if (CORRIDORS && CORRIDORS_CITY === id) return CORRIDORS;

  const built = [];
  const merged = new Set();

  for (const [suburban, partner] of throughPairsOnce()) {
    const far = lineStops(partner);
    const seen = new Set(far.map((s) => s.stopId));
    // The partner REVERSED, so the corridor reads end to end in one
    // direction, then this line's own stops with the shared city platforms
    // struck out — they are the same concrete and naming them twice would put
    // a phantom hop in the middle of every journey through the tunnel.
    const stops = [...far].reverse().concat(lineStops(suburban).filter((s) => !seen.has(s.stopId)));
    built.push(corridor(`${partner}+${suburban}`, [suburban, partner], stops));
    merged.add(suburban);
    merged.add(partner);
  }

  for (const routeId of plannableRouteIds()) {
    if (merged.has(routeId)) continue;
    built.push(corridor(String(routeId), [routeId], lineStops(routeId)));
  }

  CORRIDORS = built;
  CORRIDORS_CITY = id;
  return CORRIDORS;
}

function corridor(id, routeIds, stops) {
  const ids = [...new Set(routeIds)].sort((a, b) => a - b);
  const c = {
    id,
    routeIds: ids,
    stops,
    index: new Map(stops.map((s, i) => [s.stopId, i])),
    hasCityEnd: stops.some(isCityEnd),
    // EVERY route on it, not any: a corridor is regional when there is no
    // metropolitan train on it at all. No through-running pair mixes the two,
    // so in Melbourne this is simply whether the line is V/Line's.
    regional: ids.length > 0 && ids.every((routeId) => lineRecord(routeId)?.regional),
  };
  c.block = blockOf(c);
  return c;
}

/** The corridors passing through a station, in the order they were built. */
export function corridorsThrough(stopId) {
  const id = Number(stopId);
  return corridors().filter((c) => c.index.has(id));
}

/** One corridor by its id, or null. Legs carry the id, not the object. */
export function corridorById(id) {
  return corridors().find((c) => c.id === String(id)) ?? null;
}

/**
 * Where "the city" is on a corridor: `[firstIndex, lastIndex]`, inclusive.
 *
 * The longest contiguous run of city stations. On a line that ends in town that
 * is the leading run — Parliament through Flinders Street; on a through-running
 * corridor it is the block in the middle, North Melbourne through Anzac, and
 * the two halves of the journey sit either side of it. Longest rather than
 * first because the loop lines list Flinders Street after Parliament and a
 * shorter run somewhere else would put the city in the suburbs.
 *
 * Stony Point is the awkward one and the reason this returns a range rather
 * than a flag: it has no city end at all — ten stations from Frankston down the
 * peninsula. Its "city" is Frankston, the terminus that serves another line,
 * because that is the direction "in" means to anyone standing at Hastings.
 */
export function cityBlock(c) {
  return c?.block ?? blockOf(c);
}

function blockOf(c) {
  const stops = c?.stops ?? [];
  let best = null;
  for (let i = 0; i < stops.length; i += 1) {
    if (!isCityEnd(stops[i])) continue;
    let j = i;
    while (j + 1 < stops.length && isCityEnd(stops[j + 1])) j += 1;
    if (!best || j - i > best[1] - best[0]) best = [i, j];
    i = j;
  }
  if (best) return best;

  // No city end anywhere on it. The end station serving the most lines is the
  // one everybody is heading for; on Stony Point that is Frankston.
  const ends = [0, Math.max(0, stops.length - 1)];
  const at = ends.reduce((a, b) => ((stops[b]?.lines?.length ?? 0) > (stops[a]?.lines?.length ?? 0) ? b : a), ends[0]);
  return [at, at];
}

/** −1 before the city block, 0 inside it, +1 after. */
function side(c, i) {
  const [start, end] = cityBlock(c);
  if (i < start) return -1;
  if (i > end) return 1;
  return 0;
}

/** How many stations away from the city block, zero inside it. */
function fromBlock(c, i) {
  const [start, end] = cityBlock(c);
  if (i < start) return start - i;
  if (i > end) return i - end;
  return 0;
}

/**
 * Which way a train has to be going to carry you from one stop to the other.
 *
 * 'citybound' or 'outbound' — the vocabulary the departure board already uses,
 * so `directionOf` on a live row can be compared to it directly.
 *
 * Two rules. A journey whose ends sit on OPPOSITE sides of the city block goes
 * in first whatever else it does, so Footscray to Carnegie is citybound even
 * though Carnegie is the far end of the line. Otherwise it is simply whether
 * you finish nearer the city than you started; a trip inside the block itself
 * counts as outbound, because every train leaving Flinders Street is leaving.
 *
 * On a plain radial line this reduces to "a lower index is citybound", which is
 * how the bundle stores every line — but only because the city is at index zero
 * there, and on a tunnel corridor it is in the middle.
 */
export function directionBetween(c, fromIdx, toIdx) {
  const a = side(c, fromIdx);
  const b = side(c, toIdx);
  if (a !== 0 && b !== 0 && a !== b) return 'citybound';
  return fromBlock(c, toIdx) < fromBlock(c, fromIdx) ? 'citybound' : 'outbound';
}

// ── Planning: which trains, in which order ───────────────────────────────────

/**
 * How to get from one station to another, as itineraries of one, two or three
 * legs.
 *
 * `{ ok: true, itineraries }`, or `{ ok: false, reason }` where the reason is
 * 'same', 'unknown' or 'no-route' — each of which the screen has a sentence
 * for. No times here at all: this is the shape of the network, and answering
 * it costs nothing and needs no signal.
 *
 * Direct beats everything. If any corridor carries both stations the answer is
 * that one leg and the changes are not even considered — a change that happens
 * to be quicker on paper is not quicker once somebody has read it, walked it
 * and waited for it.
 *
 * The two-change itineraries come LAST and are meant to stay unread. They are
 * in the list because a one-change plan that no train will honour is worse
 * than a longer one that will: Carnegie to Merlynston after nine at night is
 * the case — the Upfield trains have stopped calling at Flinders Street,
 * Melbourne Central and Parliament, and the only Upfield platform left is
 * Southern Cross, which no tunnel train reaches and no subway walks to. Three
 * trains is the honest answer there and "no trains in the next two hours" is
 * a false one. `time()` reads them only after every shorter itinerary has come
 * back empty.
 *
 * What this stage produces is a PROPOSAL, and the two things it is really
 * deciding are which routes to ask the board for and which way they have to be
 * going. `time()` holds the run patterns, and a station named here as the place
 * to change moves the moment a real train disagrees.
 */
// ── Avoiding a line, or a station ────────────────────────────────────────────
//
// A disruption is not a fact about a journey until the planner can be told to
// leave something out. `avoid` is that instruction, and it has two halves
// because a fault has two shapes: `routeIds` is a line that is not running at
// all, and `stopIds` is a stretch of railway or a single station, which cuts
// EVERY line that rides through it. South Yarra shut is not a Cranbourne
// problem; it is a Cranbourne, Frankston, Pakenham and Sandringham problem,
// and only the stop knows that.
//
// A MODULE-LEVEL SET RATHER THAN A PARAMETER, for the synchronous half. Eight
// callers reach `legOn`, four of them through two more functions, and
// threading one constant through every signature would buy nothing but the
// chance to forget it at one call site. `withAvoid` sets it, runs the whole
// synchronous body, and restores it in a `finally` — so a throw cannot leave
// the next plan avoiding somebody else's lines, and two planners interleaved
// on the event loop never see each other's set, because neither of them
// awaits anything while it is held.
//
// The ASYNCHRONOUS half cannot use it, for exactly that reason: `time()` is
// awaiting boards and patterns the whole way through, and a module variable
// held across an await is a variable another caller has already changed. So
// the timing path carries the set on its `gate` and hands it to the two
// functions that need it — `matchesLeg` and `segment` — explicitly.
//
// ENDPOINTS ARE NOT TESTED. You may be standing at an avoided station, and you
// may be walking to one: what is refused is RIDING THROUGH it, and changing
// there. That distinction is the whole of the South Yarra case — somebody at
// South Yarra still has to be offered a way out of it.
let AVOID = null;

/**
 * An avoid set as the planner wants it: two Sets of numbers, or null.
 *
 * Null when there is nothing to avoid, so that every check downstream is one
 * `?.` and the no-avoidance path is byte for byte the path that existed before
 * any of this — which is what the existing suite is there to prove.
 */
export function normaliseAvoid(avoid) {
  if (!avoid) return null;
  const routeIds = new Set([...(avoid.routeIds ?? [])].map(Number).filter((n) => Number.isFinite(n)));
  const stopIds = new Set([...(avoid.stopIds ?? [])].map(Number).filter((n) => Number.isFinite(n)));
  if (!routeIds.size && !stopIds.size) return null;
  return { routeIds, stopIds };
}

/** Run a synchronous body with an avoid set in force, and always put it back. */
export function withAvoid(avoid, fn) {
  const held = AVOID;
  AVOID = normaliseAvoid(avoid);
  try {
    return fn();
  } finally {
    AVOID = held;
  }
}

const routeAvoided = (avoid, routeId) => Boolean(avoid?.routeIds?.has(Number(routeId)));
const stopAvoided = (avoid, stopId) => Boolean(avoid?.stopIds?.has(Number(stopId)));

export function plan(fromStopId, toStopId, { avoid = null } = {}) {
  return withAvoid(avoid, () => planNow(fromStopId, toStopId));
}

function planNow(fromStopId, toStopId) {
  const from = Number(fromStopId);
  const to = Number(toStopId);

  if (Number.isFinite(from) && from === to) return { ok: false, reason: 'same', itineraries: [] };

  const fromCorridors = corridorsThrough(from);
  const toCorridors = corridorsThrough(to);
  if (!stationById(from) || !stationById(to) || !fromCorridors.length || !toCorridors.length) {
    return { ok: false, reason: 'unknown', itineraries: [] };
  }

  const direct = directLegs(fromCorridors, from, to);
  if (direct.length) {
    return { ok: true, reason: null, itineraries: direct.map((leg) => itineraryOf([leg])) };
  }

  const changes = changeItineraries(fromCorridors, toCorridors, from, to);
  const offered = new Set(changes.map((i) => trainLegs(i.legs)[1]?.fromStopId).filter((id) => id != null));
  const twice = twoChangeItineraries(fromCorridors, toCorridors, from, to, offered);
  const itineraries = [...changes, ...twice];
  if (!itineraries.length) return { ok: false, reason: 'no-route', itineraries: [] };
  return { ok: true, reason: null, itineraries };
}

function itineraryOf(legs) {
  return {
    legs,
    // TRAIN changes. A walk between two halves of the same city station is one
    // interchange, not two, and a card that said "2 changes" for State Library
    // to Melbourne Central would be describing a harder journey than the one on
    // the ground.
    changes: Math.max(0, trainLegs(legs).length - 1),
    hops: legs.reduce((sum, leg) => sum + (leg.hops ?? 0), 0),
    // What it is RANKED on, which is the hops plus what a regional leg costs.
    // Reported alongside `hops` rather than instead of it: `hops` is a count of
    // stations and stays one, because the screens and the tests read it as one.
    cost: legs.reduce((sum, leg) => sum + legCost(leg), 0),
  };
}

/** Everything but the walks. */
const trainLegs = (legs) => legs.filter((leg) => leg.kind !== 'walk');

/** A leg's hops, plus `REGIONAL_HOPS` when it is a V/Line leg. */
function legCost(leg) {
  return (leg?.hops ?? 0) + (isRegionalLeg(leg) ? REGIONAL_HOPS : 0);
}

/**
 * The short leg on foot between two halves of one city station.
 *
 * `hops: 1` so that ranking pays for it — a change that needs a subway is worth
 * one station of railway against a change that does not, which is enough to
 * keep the walk from being proposed anywhere it is not needed and small enough
 * that it wins when the alternative is no journey at all.
 */
function walkLegBetween(fromStopId, toStopId) {
  return {
    kind: 'walk',
    fromStopId: Number(fromStopId),
    toStopId: Number(toStopId),
    minutes: WALK_MINUTES,
    hops: 1,
  };
}

/**
 * One leg per direction, with every corridor that can carry it folded in.
 *
 * Carnegie to State Library runs on both tunnel corridors, so the leg's
 * `routeIds` is {4, 11, 14} and the board filter at Carnegie asks for all
 * three: the person does not care which of them turns up, and asking for one
 * would drop two thirds of the trains they could catch.
 */
function directLegs(fromCorridors, from, to) {
  const byDirection = new Map();

  for (const c of fromCorridors) {
    const a = c.index.get(from);
    const b = c.index.get(to);
    if (b === undefined) continue;

    const leg = legOn(c, a, b);
    if (!leg) continue;
    const merged = byDirection.get(leg.direction);
    byDirection.set(leg.direction, merged ? mergeLegs(merged, leg) : leg);
  }

  return [...byDirection.values()].sort((x, y) => legCost(x) - legCost(y) || x.hops - y.hops);
}

/** Does the bundle say a train on this route calls at this station? */
const callsAt = (station, routeId) => (station?.lines ?? []).some((id) => Number(id) === Number(routeId));

/**
 * One leg along one corridor, or NULL where avoidance has emptied it.
 *
 * Three refusals, and they are different refusals.
 *
 * A corridor every one of whose routes is avoided carries no train at all, so
 * the leg does not exist. A corridor that only PASSES an avoided stop on the
 * way is a train that would ride through a closed station, which is the same
 * answer in a different shape. What neither refuses is either END — see the
 * note on `AVOID`.
 *
 * The third is the one the live check found. A through-running corridor
 * carries BOTH its route ids at every station on it — Carnegie sits on
 * `14+4`, so the graph will happily propose a leg from Carnegie signed
 * Sunbury. No train at Carnegie is ever signed Sunbury: every service there is
 * a Cranbourne or a Pakenham one that becomes a Sunbury one later, under a
 * second run ref, halfway across town. With those two lines suspended there is
 * no train at that platform at all, and a graph that still offered route 14
 * would have the push telling somebody standing on it to try the Sunbury line.
 *
 * So under avoidance a route survives only where the bundle's station records
 * say a train on it calls at BOTH ends of the leg. `station.lines` is the
 * ground truth for what stops where, and it is the only thing in the bundle
 * that knows the difference between a line that serves a platform and a line
 * whose id a corridor happens to carry.
 *
 * It is narrower than the truth for one case, and deliberately: a single train
 * across the tunnel is signed 4 at Carnegie and 14 at Footscray, so a Carnegie
 * to Footscray leg loses both ids and the journey has to be found as a change
 * instead. That costs a change on a screen while a line is down; the
 * alternative costs somebody a platform with no trains on it.
 */
function legOn(c, fromIdx, toIdx) {
  const lo = Math.min(fromIdx, toIdx);
  const hi = Math.max(fromIdx, toIdx);
  const via = c.stops.slice(lo + 1, hi);
  const fromId = c.stops[fromIdx].stopId;
  const toId = c.stops[toIdx].stopId;
  // A V/Line train between two metro stations is a void ticket (see
  // `vlineBarred`), so the leg never carries its line to begin with.
  const routeIds = (AVOID
    ? c.routeIds.filter((id) => !routeAvoided(AVOID, id)
      && callsAt(c.stops[fromIdx], id) && callsAt(c.stops[toIdx], id))
    : [...c.routeIds]).filter((id) => !vlineBarred(id, fromId, toId));
  if (!routeIds.length) return null;
  if (AVOID?.stopIds.size && via.some((s) => stopAvoided(AVOID, s.stopId))) return null;
  return {
    corridorId: c.id,
    routeId: routeIds[0],
    routeIds,
    fromStopId: c.stops[fromIdx].stopId,
    toStopId: c.stops[toIdx].stopId,
    direction: directionBetween(c, fromIdx, toIdx),
    hops: hi - lo,
    // In corridor order, which is the direction of travel only when the journey
    // runs that way; the journey SCREEN draws the run's own stops, never this.
    via: fromIdx <= toIdx ? via : [...via].reverse(),
  };
}

/**
 * Two legs on the same errand become one with both lines on it.
 *
 * The shorter corridor keeps the naming — its id, its `via` — because that is
 * the one whose stop list describes the journey; the route ids are unioned
 * because any train on any of them will do.
 */
function mergeLegs(a, b) {
  const keep = a.hops <= b.hops ? a : b;
  return {
    ...keep,
    routeIds: [...new Set([...a.routeIds, ...b.routeIds])].sort((x, y) => x - y),
  };
}

/**
 * One change, chosen on TOTAL hops rather than on the nearest interchange.
 *
 * This is the rule that gets Flinders Street to Carnegie right. Parliament is
 * a shared stop between the loop lines and the Pakenham corridor — an artifact
 * of PTV's stop list, since no tunnel train calls there — and it is four stops
 * from Flinders Street, so "change at the first shared station" sends somebody
 * around the loop to wait for a train that will never arrive. Counting the rest
 * of the journey too puts the change one stop away instead.
 *
 * It stays a proposal, though. Richmond carries the very same artifact — routes
 * 4 and 11 list it and no live tunnel service calls there — and no amount of
 * counting will get that out of a stop list. What knows is the run pattern, and
 * `time()` is where it is read.
 */
function changeItineraries(fromCorridors, toCorridors, from, to) {
  const byChange = new Map();

  for (const c1 of fromCorridors) {
    for (const c2 of toCorridors) {
      if (c1 === c2) continue;
      for (const best of bestInterchanges(c1, from, c2, to)) {
        const key = [best.at, best.second.fromStopId, best.first.direction, best.second.direction].join(':');
        const held = byChange.get(key);
        byChange.set(key, held
          ? {
            first: mergeLegs(held.first, best.first),
            walk: held.walk ?? best.walk,
            second: mergeLegs(held.second, best.second),
          }
          : best);
      }
    }
  }

  return [...byChange.values()]
    .map(({ first, walk, second }) => itineraryOf(walk ? [first, walk, second] : [first, second]))
    // On `cost`, not on `hops`: a Gippsland train is three stations from
    // Flinders Street to Caulfield and the tunnel is six, and the tunnel is
    // the answer. See REGIONAL_HOPS.
    .sort((x, y) => x.cost - y.cost || x.hops - y.hops)
    .slice(0, MAX_OPTIONS);
}

/**
 * The best change on this pair of corridors — and the best WALKED one as well.
 *
 * Two proposals rather than one, because the cheaper of them is regularly the
 * one that does not exist. Carnegie to Merlynston is the case: counted in
 * stations, changing at Parliament is shorter than walking the subway at Town
 * Hall, and no tunnel train has ever called at Parliament. The walked
 * alternative is carried alongside it so that a `time()` which throws the first
 * one away still has somewhere to go, and so the ranking stays honest about
 * which is shorter on paper. Both are proposals; the run pattern picks.
 */
function bestInterchanges(c1, from, c2, to) {
  const a = c1.index.get(from);
  const z = c2.index.get(to);
  const best = { plain: null, walked: null };
  // The same on every interchange of this pair, so it cannot move the choice
  // WITHIN the pair — it is here so that the totals the caller ranks pairs by
  // are all counted the same way.
  const regionalDebt = (c1.regional ? REGIONAL_HOPS : 0) + (c2.regional ? REGIONAL_HOPS : 0);

  for (const stop of c1.stops) {
    if (stop.stopId === from || stop.stopId === to) continue;
    // A change is standing on a platform and waiting, which is the one thing
    // nobody can do at a station that is shut. Riding THROUGH is refused by
    // `legOn`; this is the other half of it.
    if (stopAvoided(AVOID, stop.stopId)) continue;
    const x1 = c1.index.get(stop.stopId);
    const first = Math.abs(x1 - a);

    // Getting off here, and getting on EITHER here or on the other side of the
    // subway. State Library is not on the Upfield line and Melbourne Central is
    // — a hundred and fifty metres away, through a door, on the same ticket.
    for (const onto of boardingPoints(stop.stopId)) {
      if (onto.stopId === from || onto.stopId === to) continue;
      if (stopAvoided(AVOID, onto.stopId)) continue;
      const x2 = c2.index.get(onto.stopId);
      if (x2 === undefined) continue;

      const kind = onto.walk ? 'walked' : 'plain';
      const total = first + Math.abs(z - x2) + (onto.walk ? 1 : 0) + regionalDebt;
      const held = best[kind];
      // Ties go to the shorter first leg: the same number of stations with more
      // of them done before the change is the one where a missed connection
      // costs less.
      if (held && (total > held.total || (total === held.total && first >= held.first))) continue;
      best[kind] = { total, first, at: stop.stopId, onto: onto.stopId, x1, x2, walk: onto.walk };
    }
  }

  return [best.plain, best.walked].filter(Boolean).map((b) => ({
    at: b.at,
    first: legOn(c1, a, b.x1),
    walk: b.walk ? walkLegBetween(b.at, b.onto) : null,
    second: legOn(c2, b.x2, z),
  })).filter((best) => best.first && best.second);
}

/**
 * Where a person standing at X can get on the next train: X, and X's other half.
 *
 * `walk: false` first, always, so that every tie in every caller falls to the
 * journey that stays inside one station.
 */
function boardingPoints(stopId) {
  const points = [{ stopId: Number(stopId), walk: false }];
  const partner = walkPartner(stopId);
  if (partner) points.push({ stopId: Number(partner.stopId), walk: true });
  return points;
}

// ── Two changes ──────────────────────────────────────────────────────────────

const INTERCHANGES = new Map();

/**
 * Every point where a journey can step off one corridor and onto the next.
 *
 * `{ at, onto, x, y, walk }` — `at` is where you get off, `onto` where you get
 * on, which is the same station or the other half of it; `x` and `y` are their
 * positions on the two corridors. Memoised per ordered pair, because the
 * corridors are built once and this is the same answer every time it is asked.
 */
function interchanges(c, next) {
  const key = `${c.id}>${next.id}`;
  const held = INTERCHANGES.get(key);
  if (held) return held;

  const out = [];
  for (const stop of c.stops) {
    const x = c.index.get(stop.stopId);
    for (const onto of boardingPoints(stop.stopId)) {
      const y = next.index.get(onto.stopId);
      if (y === undefined) continue;
      out.push({ at: stop.stopId, onto: onto.stopId, x, y, walk: onto.walk });
    }
  }

  INTERCHANGES.set(key, out);
  return out;
}

/** An interchange that is not one of the two ends of the journey, nor avoided. */
const usableInterchanges = (points, from, to) => points.filter(
  (p) => p.at !== from && p.at !== to && p.onto !== from && p.onto !== to
    && !stopAvoided(AVOID, p.at) && !stopAvoided(AVOID, p.onto),
);

/** Two interchanges that touch the same station are one interchange. */
const shareAStation = (p, q) => p.at === q.at || p.at === q.onto
  || p.onto === q.at || p.onto === q.onto;

/**
 * Three trains: FROM on c1, X, c2, Y, c3 to TO — and they have to be three
 * DIFFERENT answers, not three spellings of the one that just failed.
 *
 * The middle corridor has to be a genuine middle. A c2 that carries either end
 * of the journey is rejected outright, because a journey that could have been
 * done with one change and a station to spare has already been offered above,
 * and proposing it again with an extra train on it is noise. That rule is also
 * what keeps the silly ones out — Frankston to Carnegie changing at Caulfield
 * onto a train to Malvern and back.
 *
 * Then the ranking, which is the part that matters. Counted in stations alone,
 * every one of the best three for Carnegie to Merlynston boards the Upfield
 * line at Flinders Street — the very platform the one-change plan just failed
 * on, because there is no Upfield train there after a quarter to nine. Three
 * more ways to stand on it is not an answer. So the itineraries are grouped by
 * WHERE THE LAST TRAIN IS BOARDED, one per station, with the stations a
 * one-change plan already proposed put behind the ones it did not; inside a
 * group, the fewest stations, and then the shortest hop across town, because
 * the middle train is a means and not a journey. What survives for Carnegie to
 * Merlynston is North Melbourne, Melbourne Central and Southern Cross — three
 * different Upfield platforms, which is the actual question.
 *
 * Only the ids are compared in the search, and the legs are built afterwards
 * for the few that survive: `legOn` slices a corridor's stop list, and doing
 * that inside the loop would copy a hundred thousand stations to throw away
 * ninety-nine thousand of them.
 *
 * It stays a PROPOSAL, and less of one than the shorter plans. All `time()`
 * keeps of it is the station the LAST train is boarded at; where the first
 * train can actually put you down, which line crosses town to that station and
 * where the middle train really goes are read off the runs themselves.
 */
function twoChangeItineraries(fromCorridors, toCorridors, from, to, offered = new Set()) {
  const byChange = new Map();

  for (const c1 of fromCorridors) {
    const a = c1.index.get(from);

    for (const c2 of corridors()) {
      if (c2 === c1 || c2.index.has(from) || c2.index.has(to)) continue;
      const firsts = usableInterchanges(interchanges(c1, c2), from, to).filter((p) => p.x !== a);
      if (!firsts.length) continue;

      for (const c3 of toCorridors) {
        if (c3 === c1 || c3 === c2) continue;
        const z = c3.index.get(to);
        const seconds = usableInterchanges(interchanges(c2, c3), from, to).filter((q) => q.y !== z);
        if (!seconds.length) continue;

        const debt = [c1, c2, c3].reduce((sum, c) => sum + (c.regional ? REGIONAL_HOPS : 0), 0);
        for (const p of firsts) {
          const head = Math.abs(p.x - a) + (p.walk ? 1 : 0);
          for (const q of seconds) {
            // The middle train has to move, and the two changes have to be two
            // places.
            if (q.x === p.y || shareAStation(p, q)) continue;
            const mid = Math.abs(q.x - p.y);
            const total = head + mid + (q.walk ? 1 : 0) + Math.abs(z - q.y) + debt;
            const key = `${p.at}:${p.onto}:${q.at}:${q.onto}`;
            const way = { total, mid, c1, a, p, c2, q, c3, z };
            const held = byChange.get(key);
            if (!held) byChange.set(key, { total, mid, board: q.onto, ways: [way] });
            else if (total < held.total || (total === held.total && mid < held.mid)) {
              held.total = total;
              held.mid = mid;
              held.ways.push(way);
            } else held.ways.push(way);
          }
        }
      }
    }
  }

  const best = new Map();
  for (const held of byChange.values()) {
    const seen = best.get(held.board);
    if (!seen || held.total < seen.total || (held.total === seen.total && held.mid < seen.mid)) {
      best.set(held.board, held);
    }
  }

  return [...best.values()]
    .sort((x, y) => Number(offered.has(x.board)) - Number(offered.has(y.board))
      || x.total - y.total
      || x.mid - y.mid)
    .slice(0, TWO_CHANGE_ITINERARIES)
    .map((held) => legsAcross(held.ways))
    .filter(Boolean)
    .map((legs) => itineraryOf(legs));
}

/**
 * The five legs of one two-change journey, with every corridor that can carry
 * a leg folded into it.
 *
 * Same rule as `mergeLegs` uses on one change: the person does not care which
 * of the two tunnel corridors their train is signed as, and asking the board
 * for one of them would drop half the trains they could catch. A leg whose
 * direction disagrees is left out rather than merged — two corridors can name
 * the same pair of stations and mean opposite ways round.
 */
function legsAcross(ways) {
  const ordered = [...ways].sort((a, b) => a.total - b.total || a.mid - b.mid);
  let legs = null;

  for (const w of ordered) {
    const built = [
      legOn(w.c1, w.a, w.p.x),
      ...(w.p.walk ? [walkLegBetween(w.p.at, w.p.onto)] : []),
      legOn(w.c2, w.p.y, w.q.x),
      ...(w.q.walk ? [walkLegBetween(w.q.at, w.q.onto)] : []),
      legOn(w.c3, w.q.y, w.z),
    ];
    // One refused leg is a refused journey: three trains with a hole in the
    // middle is not a shorter plan, it is a wrong one.
    if (built.some((leg) => !leg)) continue;
    if (!legs) { legs = built; continue; }
    legs = legs.map((leg, i) => (leg.kind === 'walk' || built[i].direction !== leg.direction
      ? leg
      : mergeLegs(leg, built[i])));
  }

  return legs;
}

// ── Which of the trains on the board is this leg's train ─────────────────────

/**
 * Whether a live departure could be the train for this leg.
 *
 * IT REFUSES FOR TWO REASONS, AND ONLY ONE OF THEM IS A HEURISTIC.
 *
 * The first is not. A cancelled run is not a train that might be going the
 * wrong way — PTV has said it is not coming, and no pattern call is going to
 * change that answer. It is checked first, before the route, so the refusal
 * costs nothing and so that the budget is never spent reading the stopping
 * pattern of a service that has been withdrawn.
 *
 * The second is the direction check, and it is A CALL SAVER AND NOTHING MORE.
 * It costs one signed request to fetch a run's pattern, the budget is small,
 * and a board at Richmond has thirty rows on it; this is what stops a third of
 * the budget being spent on trains heading the wrong way. Every row it lets
 * through is still gated on the pattern afterwards, so a false positive costs
 * a call and a false negative costs a train — which is why it is skipped
 * wherever it cannot be trusted rather than guessed at:
 *
 *   * boarding in the city block, where every train is leaving town and
 *     `directionOf` has nothing to bite on;
 *   * a corridor with no city end — Stony Point, where "in" is Frankston and
 *     no destination name says so.
 */
export function matchesLeg(departure, leg, fromStation, c, avoid = AVOID) {
  // FIRST LINE, on purpose. The board shows cancelled rows now — struck
  // through, because a hole in a board reads as a stale board — and a row the
  // screen is allowed to display is a row the planner must never plan onto.
  if (departure?.cancelled) return false;

  const routeId = Number(departure?.routeId);
  if (!Number.isFinite(routeId) || !leg?.routeIds?.includes(routeId)) return false;
  if (vlineBarred(routeId, leg.fromStopId, leg.toStopId)) return false;
  // AFTER the cancelled check and before anything that costs a call: a train
  // signed as a line that is not running is a board row PTV has not caught up
  // with, and reading its pattern would spend the budget proving it.
  if (routeAvoided(avoid, routeId)) return false;
  if (!c?.hasCityEnd) return true;

  // No exemption for city stations any more. It used to wave every train
  // through at a station inside the city block, which meant the two
  // connections tried at North Melbourne for an Upfield leg were both signed
  // City, both gated out by the pattern, and Carnegie to Merlynston came back
  // with nothing. The direction rule reads the destination against the
  // station's own lines and is right at North Melbourne, Richmond and the
  // loop alike; the pattern still has the last word.
  return directionOf(departure, fromStation?.lines ?? [], { stopId: fromStation?.stopId }) === leg.direction;
}

/**
 * The part of a run that is this leg, or null if the run is no use.
 *
 * Null covers three real cases and they are all the same answer to the person:
 * the train runs express through one of the two stations, it is going the other
 * way, or it terminates before it gets there. Times are the estimate where
 * there is one and the timetable otherwise, which is the same rule the board
 * uses, so a delayed train shows the same lateness in both places.
 */
export function segment(pattern, fromStopId, toStopId, avoid = AVOID) {
  const stops = Array.isArray(pattern?.stops) ? pattern.stops : [];
  const from = stops.findIndex((s) => Number(s.stopId) === Number(fromStopId));
  if (from < 0) return null;

  const to = stops.findIndex((s, i) => i > from && Number(s.stopId) === Number(toStopId));
  if (to < 0) return null;

  // The last word on avoidance, and the only one that reads a real train. The
  // graph refused the corridors; this refuses the RUN — a diverted service
  // whose pattern goes through the closed station anyway, and a run signed as
  // a line that is not meant to be running. Both halves of a stitched pattern
  // count, because a train that becomes another run of itself is one train.
  if (avoid) {
    const routes = [pattern?.routeId, pattern?.stitched?.routeId, pattern?.continues?.routeId];
    if (routes.some((id) => id != null && routeAvoided(avoid, id))) return null;
    // STRICTLY INSIDE. Boarding at an avoided stop, or getting off at one, is
    // allowed; being carried through one is not.
    for (let i = from + 1; i < to; i += 1) {
      if (stopAvoided(avoid, stops[i].stopId)) return null;
    }
  }

  // PTV stops estimating a few stops before the terminus, so a train running
  // six minutes late at Southern Cross would "arrive" at Flinders Street two
  // minutes before it left Southern Cross. The last known delay is carried
  // onto every later stop that has no estimate of its own.
  const carried = carryDelay(stops.slice(from, to + 1));
  const departAt = timeOf(carried[0]);
  const arriveAt = timeOf(carried[carried.length - 1]);
  if (!departAt || !arriveAt) return null;
  // A journey that arrives before it leaves is a run with broken times, not a
  // fast train. Better no option than one that reads as negative minutes.
  if (Date.parse(arriveAt) <= Date.parse(departAt)) return null;

  return {
    departAt,
    arriveAt,
    platform: stops[from].platform ?? null,
    stops: carried,
  };
}

/**
 * TWO HALVES OF ONE TRAIN, JOINED SO THE RESULT IS STILL A PATTERN.
 *
 * A stopping pattern has two properties a reader relies on absolutely: each
 * station appears ONCE, and the times only ever go FORWARD. Neither survives
 * naive concatenation, because PTV's two runs OVERLAP rather than meet.
 *
 * THE INCIDENT. A card for a Pakenham service out of the tunnel read:
 *
 *     Town Hall 00:13 · State Library 00:11 · Town Hall 00:13 · Anzac 00:16
 *
 * Town Hall twice at one minute, with State Library between them a minute
 * EARLIER than both. The join had assumed the feeder ends exactly where this
 * run begins — that is what `cameFrom.at` names — and it does not: the handover
 * is at Town Hall, and the outbound run's own published pattern starts one stop
 * BEFORE it, at State Library, which the feeder had already called at. So both
 * halves carried both stations and the seam folded back on itself.
 *
 * THE RULE THAT FIXES IT, and it is one rule for both directions. The run the
 * board showed is AUTHORITATIVE: its rows are the ones with this train's own
 * times on them. The other half contributes only what this run does not
 * already have — no stop it repeats, and no row that would take the clock
 * backwards. `sequence` below is that rule, applied once at the seam.
 *
 * WHY NOT TRUST `cameFrom.at`. It is PTV's own statement of where the handover
 * happens and it is correct about the HANDOVER; it is simply not a statement
 * about where either published pattern starts or ends. Using it as a cut point
 * was reading it as something it never claimed to be.
 */
function sequence(rows) {
  const out = [];
  let last = -Infinity;
  for (const stop of rows) {
    const id = Number(stop?.stopId);
    if (!Number.isFinite(id)) continue;
    const at = Date.parse(stop?.estimatedAt ?? stop?.scheduledAt ?? '');
    // THE CLOCK ONLY GOES FORWARD. A row that would take it backwards is the
    // seam folding over, never a train.
    if (Number.isFinite(at) && Number.isFinite(last) && at < last) continue;
    // AND THE SAME STATION TWICE RUNNING IS THE SEAM, NOT A JOURNEY.
    //
    // Not the same station twice in a pattern, which is an ordinary loop
    // service calling at Flinders Street on its way in and again on its way
    // round: a real pattern, half an hour apart, and dropping the second would
    // take a stop off somebody's journey. Two copies BACK TO BACK are the two
    // halves publishing one handover.
    //
    // THE LATER COPY WINS, because the later copy is the DEPARTURE. The feeder
    // arrives at Town Hall at 09:08 and this train leaves it at 09:09; the row
    // somebody is catching is the one they can still board, so the second copy
    // replaces the first rather than being dropped by it.
    if (out.length && Number(out[out.length - 1].stopId) === id) out.pop();
    if (Number.isFinite(at)) last = at;
    out.push(stop);
  }
  return out;
}

/**
 * Two runs of one train, read as one pattern.
 *
 * The junction stop closes the first run and opens the second, so it appears
 * once. The result keeps the first run's ref and route (that is the train the
 * board showed) and records where and as what it carries on, so a screen can
 * say "continues as Sunbury line" at Town Hall instead of "change".
 */
export function stitchRuns(pattern, next) {
  const a = Array.isArray(pattern?.stops) ? pattern.stops : [];
  const b = Array.isArray(next?.stops) ? next.stops : [];
  if (!a.length || !b.length) return pattern;
  const last = a[a.length - 1];
  // THIS RUN FIRST, so its own rows win every collision: they are the ones the
  // board showed and the ones carrying this train's times. `sequence` drops
  // whatever the continuation repeats and whatever would run the clock
  // backwards across the seam.
  return {
    ...pattern,
    stops: sequence([...a, ...b]),
    stitched: {
      at: Number(last.stopId),
      routeId: Number(next.routeId) || pattern.continues?.routeId || null,
      runRef: String(next.runRef ?? pattern.continues?.runRef ?? ''),
      destination: pattern.continues?.destination ?? '',
    },
    continues: next.continues ?? null,
  };
}

/**
 * The run this train came from, read as the first half of one pattern.
 *
 * The mirror of `stitchRuns`, and the same join seen from the other side. A
 * Pakenham service boarded at State Library begins its own pattern there — the
 * train came through the tunnel as a Sunbury run and PTV models that as two
 * runs meeting at the junction, so the card opened on a train with nothing
 * behind it and a live marker two stops back with no row to sit on.
 *
 * The junction stop closes the feeder and opens this run, so it appears ONCE,
 * and it belongs to this run: it is the row where the train stops being the
 * other line and starts being this one, which is what `fedFrom.at` names and
 * what the card colours the rail by.
 *
 * `pattern` keeps its own ref and route throughout — the feeder is where this
 * train has BEEN, not what it is. The board showed this run.
 */
export function stitchBefore(prev, pattern) {
  const a = Array.isArray(prev?.stops) ? prev.stops : [];
  const b = Array.isArray(pattern?.stops) ? pattern.stops : [];
  if (!a.length || !b.length) return pattern;

  // THE FEEDER KEEPS ONLY WHAT HAPPENED BEFORE THIS RUN OPENED.
  //
  // ONE RULE, AND IT IS THE CLOCK. Not "up to the junction stop", which is what
  // this did and what folded the seam over: the feeder reaches Town Hall, this
  // run begins one stop EARLIER at State Library, and cutting at Town Hall kept
  // a feeder copy of a station this run was about to publish itself. The two
  // patterns overlap rather than meet, and only the times say by how much.
  //
  // IT HANDLES THE LOOPING FEEDER TOO, which a stop-id rule cannot. A feeder
  // that called at the junction on its way round and again at the end has two
  // honest rows for one station: the early one is genuinely behind this train
  // and stays, the one at the junction is this run's to publish and goes. Same
  // rule, no special case.
  const opens = Date.parse(b[0]?.estimatedAt ?? b[0]?.scheduledAt ?? '');
  const head = Number.isFinite(opens)
    ? a.filter((s) => {
      const at = Date.parse(s?.estimatedAt ?? s?.scheduledAt ?? '');
      // A feeder row with no usable time cannot be placed against this run at
      // all. It is still behind the train, so it is kept and `sequence` puts
      // it in order with what it can measure.
      return !Number.isFinite(at) || at < opens;
    })
    : a;

  return {
    ...pattern,
    stops: sequence([...head, ...b]),
    fedFrom: {
      at: Number(b[0].stopId),
      routeId: Number(prev.routeId) || pattern.cameFrom?.routeId || null,
      runRef: String(prev.runRef ?? pattern.cameFrom?.runRef ?? ''),
    },
    // What the FEEDER came from, so a card could walk back another join. Nothing
    // does today; leaving this run's own `cameFrom` here would offer to stitch
    // the same half on twice.
    cameFrom: prev.cameFrom ?? null,
  };
}

/** The stops with a missing estimate given the delay of the last stop that had one. */
export function carryDelay(stops) {
  let delayMs = null;
  return stops.map((s) => {
    if (s.estimatedAt && s.scheduledAt) {
      delayMs = Date.parse(s.estimatedAt) - Date.parse(s.scheduledAt);
      return s;
    }
    if (delayMs == null || !s.scheduledAt || Number.isNaN(delayMs)) return s;
    const at = Date.parse(s.scheduledAt) + delayMs;
    return { ...s, estimatedAt: new Date(at).toISOString(), carried: true };
  });
}

/**
 * Stamp a planned leg with the live times from a freshly read run.
 *
 * The journey page used to keep the estimates it planned with, so an open
 * trip sat behind the Live Activity that was already reading GTFS-RT. Same
 * stops, same ids: copy the feed's `estimatedAt` onto the row the person is
 * looking at.
 */
export function applyRunTimes(leg, run) {
  if (!leg || !Array.isArray(run?.stops) || !run.stops.length) return leg;
  const byId = new Map();
  for (const stop of run.stops) {
    const id = Number(stop?.stopId);
    if (Number.isInteger(id) && id > 0) byId.set(id, stop);
  }
  const stops = (Array.isArray(leg.stops) ? leg.stops : []).map((stop) => {
    const live = byId.get(Number(stop?.stopId));
    if (!live) return stop;
    return {
      ...stop,
      estimatedAt: live.estimatedAt ?? stop.estimatedAt,
      arrivalAt: live.arrivalAt ?? stop.arrivalAt,
      departureAt: live.departureAt ?? stop.departureAt,
      atPlatform: live.atPlatform === true ? true : stop.atPlatform,
      incoming: live.incoming === true ? true : stop.incoming,
      heading: live.heading === true ? true : stop.heading,
    };
  });
  const first = stops[0];
  const last = stops[stops.length - 1];
  const scheduled = Date.parse(first?.scheduledAt ?? '');
  const estimated = Date.parse(first?.estimatedAt ?? '');
  const delayMinutes = Number.isFinite(scheduled) && Number.isFinite(estimated)
    ? Math.max(0, Math.round((estimated - scheduled) / 60_000))
    : leg.delayMinutes;
  return {
    ...leg,
    stops,
    departAt: first?.departureAt ?? first?.estimatedAt ?? first?.scheduledAt ?? leg.departAt,
    arriveAt: last?.arrivalAt ?? last?.estimatedAt ?? last?.scheduledAt ?? leg.arriveAt,
    delayMinutes,
    cancelled: Boolean(run.cancelled) || Boolean(leg.cancelled),
    atPlatform: stops.some((stop) => stop?.atPlatform),
  };
}

const timeOf = (stop) => stop?.estimatedAt ?? stop?.scheduledAt ?? null;

// ── Putting times on it ──────────────────────────────────────────────────────

/**
 * Real trains for itineraries, inside a call budget.
 *
 * `io` is the only way out of this module: `departures(stopId, routeIds)` and
 * `run(runRef)`. Both are counted, and when the budget is gone this returns
 * what it has rather than throwing — an option in hand beats an exception, and
 * the screen has no better thing to say about "we ran out of calls" than about
 * "there are no trains". A failing `io`, on the other hand, propagates: that is
 * the network being down, and the app has a sentence for that too.
 *
 * A one-change plan: departures at the boarding station (1), the candidates'
 * patterns FETCHED IN PARALLEL, a continuation apiece where the tunnel splits
 * a train into two runs, then departures at the change station and its
 * connections' patterns in parallel — a few round trips, and what a live
 * Carnegie to Flinders Street actually costs is seventeen.
 *
 * A two-change plan spends what is left, and only after the shorter ones have
 * all come back empty. Carnegie to Merlynston after nine at night costs
 * twenty-seven of the twenty-eight: thirteen to establish that no single
 * change works, and fourteen to find the three trains that do.
 */
export async function time(itineraries, {
  now = Date.now(), io, budget = CALL_BUDGET, max = MAX_OPTIONS, avoid = null, per = null,
  deadlineMs = null, enoughMs = null, onePerTrain = false, lingerMs = null, onOption = null,
  stopped = null, lastResort = false,
} = {}) {
  const startedAt = Date.now();
  const deadlineAt = Number.isFinite(deadlineMs) && deadlineMs > 0 ? startedAt + deadlineMs : null;
  const enoughAt = Number.isFinite(enoughMs) && enoughMs > 0 ? startedAt + enoughMs : null;
  const gate = callGate(io, budget, now, avoid, deadlineAt, enoughAt, stopped);
  const options = [];

  // ── ONE OPTION PER FIRST TRAIN, FOR A DEPARTURES BOARD ────────────────────
  //
  // A full plan (Options, or the board's "plan the trip" when its own quick
  // plan came back empty) wants the best three journeys, and three journeys
  // that all start on the train at the platform are a fine answer there: the
  // train is the one to catch. A BOARD annotates ROWS, and a row
  // is a train. Three options on one train is one annotated row and two
  // trains below it filed under "Not to Wyndham Vale" when both of them pass
  // Sunshine too. So the board asks for a different question: the next few
  // trains out of here that can each be turned into a journey, one journey
  // apiece. `taken` is the first-train refs that already have one, and a
  // candidate in it is not followed again — by the next itinerary either.
  //
  // It also does not stop on `hurry`. The first option is on the screen the
  // moment it exists (`onOption`), so nobody is watching a loader while the
  // second and third are found; the budget, the deadline, a busy gate and
  // `stopped` still end it.
  //
  // `lingerMs` is how long, from the start, a plan that ALREADY has an option
  // may keep going. The deadline is a promise about the loader, and once
  // there is an answer on screen the loader is gone: the extra trains get
  // until here instead. Absent, the deadline is the deadline.
  const taken = new Set();
  const found = [];
  const firstRef = (option) => {
    const leg = trainLegs(option?.legs ?? [])[0];
    return leg?.runRef == null ? null : String(leg.runRef);
  };
  const report = (option) => {
    if (!option) return;
    const ref = firstRef(option);
    if (onePerTrain && ref) taken.add(ref);
    if (onePerTrain && lingerMs > 0 && !found.length) gate.extend(startedAt + lingerMs);
    found.push(option);
    if (typeof onOption === 'function') {
      try {
        onOption(finish(found));
      } catch {
        // A screen that cannot draw the progress does not get to end the plan.
      }
    }
  };
  // One per first train when that is the question, best first either way.
  const finish = (list) => {
    const ranked = rank(dedupe(list));
    if (!onePerTrain) return ranked.slice(0, max);
    const seen = new Set();
    return ranked.filter((o) => {
      const ref = firstRef(o);
      if (ref == null) return true;
      if (seen.has(ref)) return false;
      seen.add(ref);
      return true;
    }).slice(0, max);
  };
  // Every row this plan saw on the board it boards from, and which of them are
  // heading a way some itinerary could use — the rest are known to be no use.
  const origin = new Map();
  // The first trains this plan followed all the way and got nothing from.
  const tried = new Set();

  // Direct first, then the fewest hops: the itinerary most likely to produce a
  // usable option gets the budget before the ones that need two trains.
  const ordered = [...itineraries].sort((a, b) => a.changes - b.changes || a.hops - b.hops);
  const near = ordered.filter((i) => (i.changes ?? 0) < 2);
  const far = ordered.filter((i) => (i.changes ?? 0) >= 2);

  // THE ONE PLACE A SPECIAL CAN BE PICKED UP, decided before a single request
  // goes out and at no cost at all. `probeRoutes` is empty at every station but
  // the handful PTV lists an event route at, so on every ordinary trip the next
  // three lines change nothing: same board, same filter, same call count.
  const ends = commonEnds(itineraries);
  const probe = ends ? probeRoutes(ends.from, ends.to) : [];
  // The itinerary whose board request is widened. It must start where the trip
  // starts — a plan can propose a walk to the station next door — and it is the
  // most direct one, which is the one `spend` reaches first and therefore the
  // one whose board is in the gate's cache by the time the special pass asks.
  const probeOn = probe.length
    ? ordered.find((i) => Number(i.legs[0]?.fromStopId) === ends.from) ?? null
    : null;
  const originRoutes = probeOn
    ? [...new Set([...(probeOn.legs[0].routeIds ?? []), ...probe])]
    : null;

  // The same train reached by two itineraries is one option, so the cap counts
  // DISTINCT options. Without this a second look at an itinerary that has
  // already been timed fills the cap with the answer it already has.
  const settled = () => options.splice(0, options.length, ...dedupe(options));
  // What each itinerary has already produced, so that a second look asks for
  // that many MORE rather than for the same ones again.
  const made = new Map();

  const spend = async (list, cap, per, extra = 0) => {
    for (const itinerary of list) {
      if (options.length >= cap || gate.spent()) break;
      // AN ANSWER IN HAND BEATS A BETTER ONE NOT YET ASKED FOR. The next
      // itinerary is another board and another pattern, which through a busy
      // gate is another seven seconds of loader over an option already on the
      // card. See PLAN_ENOUGH_MS. A busy gate with NOTHING on the card is the
      // other case: one 503 used to set the budget spent and leave Carnegie
      // to Southern Cross empty, so we only stop looking once there is
      // something to show.
      if (options.length && ((!onePerTrain && gate.hurry()) || gate.busy)) break;
      const room = Math.min(per, cap - options.length);
      const got = await timeItinerary(itinerary, {
        now,
        gate,
        max: room + (made.get(itinerary) ?? 0),
        routes: itinerary === probeOn ? originRoutes : null,
        onePerTrain,
        taken,
        report,
        tried,
        extra,
        board: (rows) => { for (const d of rows) if (d?.runRef) origin.set(String(d.runRef), d); },
      });
      made.set(itinerary, (made.get(itinerary) ?? 0) + got.length);
      options.push(...got);
      settled();
    }
  };

  // `per` caps what ONE itinerary may fill, and exists for the reroute engine.
  // Four itineraries to four different stations, asked for six options, and the
  // first station takes all six: the card then offers five Frankston trains to
  // the same platform and never asks the Glen Waverley board at all. A cap of
  // two spreads the same budget across the places somebody could actually be
  // dropped. Null is the behaviour every other caller has always had.
  // THE SPECIAL FIRST, and that order is the whole point rather than a
  // preference. It asks for the SAME board the first itinerary is about to ask
  // for, so it costs nothing to ask it here instead of later, and the
  // itineraries then read that board out of the gate's cache. Asked afterwards
  // it was never asked at all: South Yarra to Dandenong on a match night has
  // five ordinary options via Caulfield, the cap was full before the special
  // pass ran, and the direct train standing on platform 6 was the one thing the
  // screen did not say. `rank` decides what wins; this decides what is offered.
  if (originRoutes && !gate.spent()) {
    const specials = await timeSpecials(ends, {
      now, gate, routes: originRoutes, max: SPECIAL_CANDIDATES,
    });
    specials.forEach(report);
    options.push(...specials);
  }

  // ── ONE LOOK AT EACH WAY OF STARTING, BEFORE ANY WAY GETS A SECOND ─────────
  //
  // The itineraries are ranked on paper, and the first of them used to be asked
  // for everything: `max` options, all of them off ITS board. Richmond to
  // Carnegie is the case. The cheapest proposal on paper leaves by the citybound
  // trains and changes at Flinders Street, so the board's three annotated rows
  // were the next three citybound trains and the plan never once asked about
  // the Frankston train at the next platform, which changes at Caulfield onto a
  // train that runs straight to Carnegie. It was proposed — it was fifth in the
  // list — and never timed, so it never annotated its row and stayed dimmed.
  //
  // So the first pass takes the best proposal of each DISTINCT start — a
  // different line or direction out of the boarding station, which is a
  // different board and a different set of trains — and asks each for ONE
  // option, all at once. `rank` then chooses between them on when they arrive,
  // the only fair way to compare a change at Flinders Street with one at
  // Caulfield. The second pass gives what is left of the cap to the best
  // proposals, exactly as before.
  //
  // CAPPED AT `max - 1` STARTS, so the best start keeps a second train, and
  // ranked on `cost` rather than `hops`, so a regional leg — a ten-station
  // debt — cannot use up one of them. Only for changes: a direct journey has
  // nothing to compare, and a caller that passed `per` is the reroute engine,
  // which spreads its own budget.
  if (per == null) {
    const starts = [];
    const seen = new Set();
    const byCost = [...near].sort((a, b) => a.changes - b.changes || a.cost - b.cost || a.hops - b.hops);
    for (const itinerary of byCost) {
      if (!(itinerary.changes > 0)) continue;
      const leg = itinerary.legs[0];
      const key = [leg.fromStopId, leg.direction, [...(leg.routeIds ?? [])].sort((x, y) => x - y).join(',')].join(':');
      if (seen.has(key)) continue;
      seen.add(key);
      starts.push(itinerary);
    }
    const wide = starts.slice(0, Math.max(1, max - 1));
    if (wide.length > 1) {
      const firsts = await Promise.all(wide.map((itinerary) => (gate.spent() ? [] : timeItinerary(itinerary, {
        now,
        gate,
        max: 1,
        routes: itinerary === probeOn ? originRoutes : null,
        onePerTrain,
        taken,
        report,
        tried,
        board: (rows) => { for (const d of rows) if (d?.runRef) origin.set(String(d.runRef), d); },
      }))));
      wide.forEach((itinerary, i) => made.set(itinerary, firsts[i].length));
      options.push(...firsts.flat());
      settled();
    }
  }

  await spend(near, max, per ?? max);

  // ONLY when nothing shorter ran. Two changes are never offered alongside one
  // — they are what the screen says instead of "no trains in the next two
  // hours", and a person who has a one-change train to catch must not be shown
  // a three-train journey next to it just because it happens to arrive a
  // minute earlier. The budget follows the same rule: none of it is spent here
  // until a plan has already failed.
  // The two-change stage gets a few calls, not the rest of the budget. It
  // wanders: when the first train's change comes back with nothing it tries the
  // next train, and that train's other change stations, and each is a board.
  // Carnegie to Union spent eight calls that way and had none left for the
  // route that works. What it does not get to is still there for the stage
  // after it, and the boards it did read are cached.
  if (!options.length) {
    // The runway is lengthened BEFORE the two-change stage, not after it: by the
    // time that stage has spent its calls the ordinary deadline has usually
    // passed, and a deadline that has passed cannot be extended.
    if (lastResort) {
      gate.extend(startedAt + THREE_CHANGE_DEADLINE_MS);
      gate.cap(gate.calls + TWO_CHANGE_STAGE_CALLS);
    }
    await spend(far, Math.min(max, TWO_CHANGE_OPTIONS), 1);
    gate.cap(Infinity);
  }

  // ── THE LAST RESORT: ONE MORE TRAIN ────────────────────────────────────────
  //
  // Everything shorter came back empty, and the gate has not said no. Run the
  // same itineraries once more with the tail allowed to be a train that itself
  // needs a change — see `THREE_CHANGE_CALLS`. Boards and patterns already read
  // are in the gate's cache and cost nothing to read again, so this pays only
  // for what is new; it is given a few more calls and a longer deadline
  // because it is only ever reached by a plan with nothing to show.
  if (lastResort && !options.length && !gate.spent() && !gate.busy && !per) {
    gate.allow(THREE_CHANGE_CALLS);
    await spend(near, max, 1, 1);
    if (!options.length) await spend(far, Math.min(max, TWO_CHANGE_OPTIONS), 1, 1);
  }

  return {
    options: finish(options),
    calls: gate.calls,
    busy: gate.busy,
    slow: gate.expired,
    wrongWay: ends ? wrongWayRows(origin, ordered, ends.from, gate.avoid) : [],
    // ONLY FROM A PLAN THAT FINISHED ON ITS OWN TERMS. A connection that came
    // back null because the budget ran out, the deadline landed or the gate
    // said busy is not a train with no journey; it is a train nobody finished
    // asking about. And a train that starts ANY option is never on this list,
    // whatever another itinerary made of it.
    noOption: gate.spent() || gate.busy
      ? []
      : [...tried].filter((ref) => !options.some((o) => firstRef(o) === ref)),
  };
}

/**
 * The rows on the boarding board that no itinerary of this plan could start on.
 *
 * `matchesLeg` against EVERY itinerary's first train, near and far, whether or
 * not the plan got round to it: pure, so asking costs nothing, and a row it
 * lets through for any one of them is not a row anybody can call useless. What
 * is left is a train on a line that goes nowhere near this journey, or one
 * heading the other way — the Cranbourne train out of Carnegie when the
 * journey is to Wyndham Vale. That is the one thing a board may head
 * "Not to Wyndham Vale" on the planner's say-so.
 */
function wrongWayRows(rows, itineraries, fromStopId, avoid) {
  const station = stationById(fromStopId);
  const firsts = itineraries
    .map((i) => i.legs?.[0])
    .filter((leg) => leg && leg.kind !== 'walk' && Number(leg.fromStopId) === Number(fromStopId));
  if (!firsts.length) return [];
  const out = [];
  for (const [ref, d] of rows) {
    if (d.cancelled) continue;
    const usable = firsts.some((leg) => matchesLeg(d, leg, station, corridorById(leg.corridorId), avoid));
    if (!usable) out.push(ref);
  }
  return out;
}

/**
 * The two ends every itinerary in this plan shares, or null.
 *
 * `plan` answers one question — from here to there — so in practice they always
 * agree; the check is here because the reroute engine hands `time` itineraries
 * to several different places at once, and a special is a claim about ONE
 * journey. Where the ends differ there is no such journey and nothing is asked.
 */
function commonEnds(itineraries) {
  const rows = (itineraries ?? []).filter((i) => i?.legs?.length);
  if (!rows.length) return null;
  const endsOf = (i) => ({
    from: Number(i.legs[0].fromStopId),
    to: Number(i.legs[i.legs.length - 1].toStopId),
  });
  const first = endsOf(rows[0]);
  if (!Number.isFinite(first.from) || !Number.isFinite(first.to)) return null;
  return rows.every((i) => {
    const e = endsOf(i);
    return e.from === first.from && e.to === first.to;
  }) ? first : null;
}

/**
 * A direct option on a train the network file says cannot exist.
 *
 * Every word of it comes off a live run. The board says a train on a route this
 * station does not serve is standing here; its own pattern says it calls at
 * both ends of the journey, in that order; and the times are the ones on that
 * pattern. Take the run away and there is no option — nothing here is built
 * from the bundle, which is the difference between reading a special and
 * inventing one.
 *
 * AT MOST TWO PATTERN CALLS, and only once a row has actually turned up. Six
 * trains is a whole event's worth; the first two are the ones anybody standing
 * on the platform is going to catch, and each is a call a normal Tuesday never
 * spends.
 */
async function timeSpecials({ from, to }, { now, gate, routes, max }) {
  const rows = await gate.departures(from, routes);
  if (!rows?.length) return [];

  const serves = stationLineIds(stationById(from)?.lines);
  const candidates = specialRows(rows, serves, SPECIAL_CANDIDATES)
    .filter((d) => !vlineBarred(d.routeId, from, to));
  if (!candidates.length) return [];

  const patterns = await Promise.all(candidates.map((d) => gate.run(d.runRef, d.routeId)));

  const built = [];
  for (const [i, departure] of candidates.entries()) {
    if (built.length >= max) break;
    const pattern = patterns[i];
    // THE RUN WITHDRAWS ITS OWN HINT where it can. A row can be off this
    // station's lines and still be an ordinary through-running train, and the
    // pattern is what knows the difference.
    const calls = offLineStops(pattern);
    if (!calls.length) continue;
    const seg = segment(pattern, from, to, gate.avoid);
    if (!seg) continue;
    built.push(specialOption({ pattern, departure, seg, calls, from, to, now }));
  }
  return built;
}

/** One train, one leg, and the observation that put it on the screen. */
function specialOption({ pattern, departure, seg, calls, from, to, now }) {
  const routeId = Number(pattern?.routeId) || Number(departure?.routeId) || null;
  const leg = {
    // NO CORRIDOR. There is no corridor: the graph has no edge between these
    // two stations, which is the whole reason this option exists. Everything
    // downstream reads `corridorId` through `corridorById`, which answers null
    // for one it does not know, and every one of those readers already handles
    // a null corridor because a walk leg has never had one either.
    corridorId: null,
    routeId,
    routeIds: [routeId].filter(Boolean),
    fromStopId: Number(from),
    toStopId: Number(to),
    direction: null,
    hops: Math.max(1, seg.stops.length - 1),
    via: seg.stops.slice(1, -1).map((s) => stationById(s.stopId)).filter(Boolean),
    // What was observed, carried on the leg so the journey screen can say where
    // this came from without re-deriving it from a pattern it does not hold.
    special: {
      line: lineName(routeId) ?? '',
      stops: calls.map((s) => s.name),
    },
  };
  return { ...optionOf([optionLeg(leg, departure, pattern, seg)], now), special: true };
}

async function timeItinerary(itinerary, {
  now, gate, max, routes = null, onePerTrain = false, taken = new Set(), report = null, board = null,
  tried = new Set(), extra = 0,
}) {
  const first = itinerary.legs[0];
  const changes = itinerary.changes ?? 0;
  const deep = changes > 1;
  const destination = itinerary.legs[itinerary.legs.length - 1].toStopId;
  const station = stationById(first.fromStopId);
  const c = corridorById(first.corridorId);

  // `routes` is the widened filter where this itinerary's board is also the one
  // a special would be standing on. It adds route ids with no scheduled service
  // at this stop, so it adds no rows on an ordinary day and no calls on any day
  // — and `matchesLeg` below refuses whatever it does bring back, because a
  // special is not on this leg's lines. It is read by `timeSpecials`, off the
  // same cached board.
  const rows = await gate.departures(first.fromStopId, routes ?? first.routeIds);
  if (!rows) return [];
  if (typeof board === 'function') board(rows);

  // `matchesLeg` refuses a cancelled run on its first line, so the second test
  // here is belt and braces — and it is stated anyway, because this is one of
  // the two places in the file that decides which board rows become candidates
  // and a reader should not have to open another function to learn that a
  // withdrawn service cannot be one.
  const wanted = rows.filter((d) => d?.runRef && !d.cancelled && matchesLeg(d, first, station, c, gate.avoid));
  // A train that already starts a journey is not a candidate twice when the
  // question is one journey per train. See `onePerTrain` in `time`.
  const fresh = onePerTrain ? wanted.filter((d) => !taken.has(String(d.runRef))) : wanted;
  const candidates = fresh.slice(0, deep ? TWO_CHANGE_CANDIDATES : candidateCount(c, first));

  // What this train would make of the journey. Pure, and asked of every
  // pattern that arrives, so it is worth a name of its own.
  const shapeOf = (pattern) => (deep
    ? shapeAlong(pattern, itinerary, { corridor: c, from: first.fromStopId, avoid: gate.avoid })
    : shapeOn(pattern, {
      corridor: c, from: first.fromStopId, destination, avoid: gate.avoid,
    }));

  // One candidate's pattern, followed onto its next run where that can change
  // the answer.
  //
  // A train that does not reach the destination on this run but carries on as
  // another one — the tunnel's two-run modelling — is read as one train,
  // which is what it is.
  //
  // ── AND IT IS THE MOST EXPENSIVE HABIT THIS PLANNER HAS ────────────────────
  //
  // It used to be one extra call for EVERY candidate, which since the tunnel
  // opened means every candidate: no Cranbourne or Pakenham run out of
  // Carnegie ends anywhere near a destination on the other side of town, so
  // five candidates were ten calls before a single option existed. Against a
  // gate that allows sixteen a minute for the whole app, that is the plan
  // spending its entire budget on first legs and being refused at the change.
  //
  // So the continuation is followed whenever this run does not already reach
  // the destination. Skipping it on the second and third trains used to save
  // a call; since the tunnel opened that "saving" is a Town Hall change
  // search — a board and another pattern — for a train that is still the
  // same Sunbury run if you just read the other half. Carnegie to State
  // Library found the first train in four seconds and then spent the other
  // sixteen on those fake changes.
  const read = async (departure) => {
    const pattern = await gate.run(departure.runRef, departure.routeId);
    if (!pattern?.continues?.runRef) return pattern;
    if (segment(pattern, first.fromStopId, destination, gate.avoid)) return pattern;
    const next = await gate.run(pattern.continues.runRef, pattern.continues.routeId ?? pattern.routeId);
    return next ? stitchRuns(pattern, next) : pattern;
  };

  const built = [];
  const take = async (shape) => {
    const option = await optionFrom(shape, {
      now,
      gate,
      destination,
      changes: (deep ? 2 : 1) + extra,
      connections: deep ? TWO_CHANGE_CONNECTIONS : CONNECTIONS,
      spread: deep ? TWO_CHANGE_POINTS : CHANGE_POINTS,
    });
    if (!option) {
      tried.add(String(shape.departure.runRef));
      return;
    }
    built.push(option);
    if (typeof report === 'function') report(option);
  };
  // A train whose own pattern answered and made nothing of this journey. Only
  // a pattern that ARRIVED counts: a null one is the budget talking, not the
  // train. `time` decides what this is worth; see `noOption` there.
  const noShape = (departure, pattern, shape) => {
    if (pattern && !shape) tried.add(String(departure.runRef));
  };

  // ── THE FIRST TRAIN IS FOLLOWED THROUGH ON ITS OWN ─────────────────────────
  //
  // Everything below fetches the other candidates' patterns together and ranks
  // them before spending a call on any of them, and that ranking is worth
  // having — at Flinders Street four of five trains can only be left at
  // Richmond, which no Dandenong service has called at since the tunnel
  // opened. What it is not worth is the WAIT. Three patterns and a
  // continuation are four calls, and through a gate that hands this app one
  // call every three and three quarter seconds they are eleven seconds in
  // which the screen has nothing at all — before the change station has even
  // been asked about.
  //
  // So the soonest train, which is the one somebody at a barrier is most
  // likely to catch, is read and finished first: its pattern, its change
  // station, its connection. On Carnegie to Southern Cross that is an option
  // on the card in about seven seconds instead of nineteen. It costs nothing
  // when it works, and when it does not the candidates behind it are read and
  // ranked exactly as they always were.
  const lead = candidates.length ? await read(candidates[0]) : null;
  const leadShape = lead && shapeOf(lead);
  if (candidates.length) noShape(candidates[0], lead, leadShape);
  if (leadShape && built.length < max) {
    await take({ ...leadShape, departure: candidates[0], pattern: lead });
  }

  // A DEAD END, NOT A BAD TRAIN. The soonest train was followed the whole way —
  // through the tunnel's handover, so its stops are the journey's — and made no
  // journey, because every change station it could have used has a board with
  // nothing on it for the line it needs. The trains behind it run the same
  // stretch and call at the same places, so reading their patterns is calls
  // spent confirming it; carnegie to Union after nine spent four of them, about
  // seventeen seconds, and never reached the route that works. Only a train
  // that was followed through counts: one that ended at the handover says
  // nothing about the ones that carry on.
  if (!built.length && lead?.stitched && leadShape?.second) {
    const tried = [leadShape, ...(leadShape.alternatives ?? [])].slice(0, deep ? TWO_CHANGE_POINTS : CHANGE_POINTS);
    if (tried.every((sh) => sh.second && gate.isEmpty(sh.second.fromStopId, connectionRoutes(sh.second)))) {
      return built;
    }
  }

  // What each of the others would make of the journey, worked out BEFORE any
  // of them is followed up, because the follow-up is what costs a call. The
  // one that gets furthest goes first, for the reason above.
  //
  // Later trains only when they are cheap. A change already in hand is the
  // same walk ten minutes later; a through train already in hand only needs
  // siblings that reach on their own pattern — following their
  // continuations was four more seconds of FINDING TRAINS for the same
  // journey. Rest is fully followed only when the soonest train could not
  // do the trip at all.
  const leadIsDirect = Boolean(leadShape && leadShape.remaining === 0);

  // ── A BOARD WANTS THE NEXT TRAINS, NOT A BETTER FIRST ONE ──────────────────
  //
  // With the soonest train already an option, a full plan stops here: the
  // rest would be the same change ten minutes later. On a board they are
  // exactly what is missing — each is its own row, and each row left without
  // a journey reads as a train that cannot take you there. So the others are
  // followed IN DEPARTURE ORDER, the order the board lists them in, and each
  // is taken as far as it goes. Cheap after the first: the change station's
  // board is already in the gate's cache, and a second train into the same
  // connection reads that connection's pattern out of it too — usually two
  // calls a train, its pattern and its continuation.
  if (onePerTrain && built.length && candidates.length > 1) {
    for (const departure of candidates.slice(1)) {
      if (built.length >= max || gate.spent() || gate.busy) break;
      const pattern = await read(departure);
      const shape = pattern && shapeOf(pattern);
      noShape(departure, pattern, shape);
      if (shape) await take({ ...shape, departure, pattern });
    }
    return built;
  }

  if (!built.length && (onePerTrain || !gate.hurry()) && candidates.length > 1) {
    const rest = await Promise.all(candidates.slice(1).map((d) => read(d)));
    const shapes = candidates.slice(1)
      .map((departure, k) => {
        const shape = shapeOf(rest[k]);
        noShape(departure, rest[k], shape);
        return shape && { ...shape, departure, pattern: rest[k] };
      })
      .filter(Boolean)
      .sort((a, b) => a.remaining - b.remaining
        || Date.parse(a.board.departAt) - Date.parse(b.board.departAt));

    for (const shape of shapes) {
      if (built.length >= max) break;
      // The same rule one level down, and this is where it usually bites: three
      // trains off one board are three options out of one itinerary, and the
      // second and third are each a connection to confirm.
      if (built.length && !onePerTrain && gate.hurry()) break;
      await take(shape);
    }
  } else if (built.length && leadIsDirect && !gate.hurry() && candidates.length > 1) {
    const rest = await Promise.all(candidates.slice(1).map((d) => gate.run(d.runRef, d.routeId)));
    const shapes = candidates.slice(1)
      .map((departure, k) => {
        const shape = shapeOf(rest[k]);
        return shape && shape.remaining === 0 && { ...shape, departure, pattern: rest[k] };
      })
      .filter(Boolean)
      .sort((a, b) => Date.parse(a.board.departAt) - Date.parse(b.board.departAt));

    for (const shape of shapes) {
      if (built.length >= max || gate.hurry()) break;
      await take(shape);
    }
  }
  return built;
}

/**
 * What one train would make of this journey, and how much would be left after.
 *
 * Pure — the pattern is already in hand — so every candidate can be compared
 * before any of them costs a second call.
 *
 * The graph proposed a leg, and by here we know which train would carry it and
 * where that train actually goes — which is not always what the stop list said.
 * Two live examples from the day this was written, both of them the common case
 * rather than an edge:
 *
 *   * PTV models the tunnel as TWO runs. The 08:37 from Carnegie is signed City
 *     and its pattern ends at Town Hall; the continuation to State Library,
 *     Parkville and Sunbury is a different run ref. "Carnegie to Footscray" is
 *     one railway journey and two runs, so it is planned as a change at Town
 *     Hall — which is what the platform indicator says too.
 *   * PTV's own stop lists for routes 4 and 11 still carry Richmond, South
 *     Yarra and Armadale, and on an ordinary day no train in the two-hour
 *     window calls at any of them — the everyday service is in the tunnel. The
 *     exception is football: scheduled event specials do run out via Richmond
 *     and South Yarra after a match at the Melbourne Cricket Ground, six or so
 *     at a time on event dates only (see `NOT_CALLED_AT` in
 *     `scripts/fetch-network.mjs`). Both facts point the same way here: a
 *     change station taken from the stop list has to be allowed to move,
 *     because the change happens where THIS train stops, and this train's own
 *     pattern is the only thing that knows whether that includes South Yarra
 *     tonight.
 *
 * So the order is: the whole journey on this one train if it goes there, else a
 * change at the stop on THIS RUN that leaves the least railway to cover, else
 * nothing at all and the next candidate gets its turn.
 */
function shapeOn(pattern, { corridor, from, destination, points = CHANGE_POINTS, avoid = null }) {
  return withAvoid(avoid, () => shapeOnNow(pattern, { corridor, from, destination, points }));
}

function shapeOnNow(pattern, { corridor, from, destination, points }) {
  const whole = segment(pattern, from, destination);
  if (whole) {
    const leg = legTo(corridor, from, destination);
    if (leg) return { leg, board: whole, walk: null, second: null, remaining: 0 };
  }

  const splits = changesOnPattern(corridor, from, pattern, destination, { max: points });
  const shapes = splits.map((split) => {
    const board = segment(pattern, from, split.first.toStopId);
    return board && {
      leg: split.first,
      board,
      walk: split.walk,
      second: split.second,
      remaining: split.second.hops + (split.walk ? 1 : 0),
    };
  }).filter(Boolean);
  if (!shapes.length) return null;
  // The best on paper first; the others are tried only if it finds no train.
  return { ...shapes[0], alternatives: shapes.slice(1) };
}

/**
 * The first train of a TWO-change journey, and where it can put you down.
 *
 * The same rule as `shapeOn`, aimed one train short. The graph is trusted for
 * ONE thing here — which platform the last train is boarded at — and for
 * nothing else: where this train stops, and which line crosses town to that
 * platform, are both read off the run and the network. Carnegie to Merlynston
 * is the case again. On paper the cheapest two-change journeys all start by
 * changing at Richmond, and no tunnel train has called at Richmond since the
 * tunnel opened; on the run, the stop that can reach Southern Cross in one
 * more train is Town Hall, a subway's walk from Flinders Street.
 */
function shapeAlong(pattern, itinerary, { corridor, from, avoid = null }) {
  return withAvoid(avoid, () => shapeAlongNow(pattern, itinerary, { corridor, from }));
}

function shapeAlongNow(pattern, itinerary, { corridor, from }) {
  const last = itinerary.legs[itinerary.legs.length - 1];
  if (!last || trainLegs(itinerary.legs).length < 3) return null;

  const tail = last.hops ?? 0;
  const riding = itinerary.legs[0].routeIds ?? [];
  const shapes = stepsOnPattern(corridor, from, pattern, last.fromStopId, { riding }).map((step) => {
    const board = segment(pattern, from, step.first.toStopId);
    return board && {
      leg: step.first,
      board,
      walk: step.walk,
      second: step.second,
      remaining: step.cost + tail,
    };
  }).filter(Boolean);

  if (!shapes.length) return null;
  return { ...shapes[0], alternatives: shapes.slice(1) };
}

/**
 * Where to get off THIS run to pick up the middle train of a two-change plan.
 *
 * The same question `changesOnPattern` asks, aimed one train short: from which
 * of the stops this train actually makes can ONE more train reach the platform
 * the last leg boards at. The graph is trusted for that platform and for
 * nothing else — not for which line crosses town to it, and certainly not for
 * where this train stops.
 *
 * Scored the same way too — on what is LEFT rather than on the whole trip.
 * Riding further on a train somebody is already sitting in is close to free,
 * and counting it sends them off at Caulfield onto a Frankston service that
 * crawls round the loop when the tunnel train they are on reaches the same
 * city in half the time.
 *
 * A stop that IS the aim is passed over, and that is not an optimisation: it
 * would mean this train already reaches the platform the last leg boards at,
 * which is the one-change journey — tried, and come back with no train, which
 * is the only reason any of this is running.
 */
function stepsOnPattern(c, fromStopId, pattern, aimStopId, { riding = [], max = TWO_CHANGE_CONNECTIONS } = {}) {
  const stops = pattern?.stops ?? [];
  const board = stops.findIndex((s) => Number(s.stopId) === Number(fromStopId));
  const aim = Number(aimStopId);
  if (board < 0 || !Number.isFinite(aim)) return [];

  const found = new Map();
  for (let i = board + 1; i < stops.length; i += 1) {
    const stopId = Number(stops[i].stopId);
    if (stopId === Number(fromStopId) || stopId === aim) continue;
    for (const onto of boardingPoints(stopId)) {
      if (onto.stopId === Number(fromStopId) || onto.stopId === aim) continue;
      const [hop] = directLegs(corridorsThrough(onto.stopId), onto.stopId, aim);
      if (!hop) continue;
      // Getting off a train to wait for another one on the same line is not a
      // change, it is a delay. Arden is one stop from North Melbourne on the
      // tunnel corridor's stop list, so without this the plan is "leave the
      // Sunbury train at Arden and catch the next Sunbury train" — three calls
      // to be told what the pattern in hand already says.
      if (hop.routeIds.every((id) => riding.includes(id))) continue;

      const first = legTo(c, fromStopId, stopId);
      if (!first) continue;

      const cost = legCost(hop) + (onto.walk ? 1 : 0);
      const have = found.get(onto.stopId);
      if (have && have.cost <= cost) continue;
      found.set(onto.stopId, {
        cost,
        order: i,
        first,
        walk: onto.walk ? walkLegBetween(stopId, onto.stopId) : null,
        second: hop,
      });
    }
  }

  return [...found.values()]
    .sort((a, b) => a.cost - b.cost
      || Number(Boolean(a.walk)) - Number(Boolean(b.walk))
      || a.order - b.order)
    .slice(0, max);
}

/** A shape, plus the connections it needs when it is not the whole journey. */
async function optionFrom(shape, {
  now, gate, destination = null, changes = 1, connections = CONNECTIONS, spread = CHANGE_POINTS,
} = {}) {
  const legs = await legsFrom(shape, { gate, destination, changes, connections, spread });
  return legs ? optionOf(legs, now) : null;
}

/**
 * One train, and then the rest of the journey behind it.
 *
 * `changes` is how many MORE trains this is allowed to add. One is the whole
 * app until tonight; two is the fallback, and the recursion is the point — the
 * second train is read exactly the way the first one was, by its own pattern,
 * so "change where this train stops" holds at the second change as well as at
 * the first.
 *
 * The walk is the first thing that happens after a train gets in, so it is
 * what the connection is measured from: arrival, plus the five minutes on
 * foot. The station's own change minutes are NOT added on top of a walk — the
 * walk is the change, and see the note by `changeMinutes` — but the person's
 * margin is, and is passed down as `walked`. A train leaving Melbourne Central
 * three minutes after the tunnel train reaches State Library is not a
 * connection, and offering it puts somebody in a subway watching their train
 * go.
 */
async function legsFrom(shape, { gate, destination, changes, connections, spread, readyAt = null }) {
  if (!shape.second) {
    return [optionLeg(shape.leg, shape.departure, shape.pattern, shape.board, readyAt)];
  }
  if (changes < 1) return null;

  for (const candidate of [shape, ...(shape.alternatives ?? [])].slice(0, spread)) {
    const walk = candidate.walk ? timedWalk(candidate.walk, candidate.board.arriveAt) : null;
    const ready = walk ? walk.arriveAt : candidate.board.arriveAt;
    const head = [
      optionLeg(candidate.leg, shape.departure, shape.pattern, candidate.board, readyAt),
      ...(walk ? [walk] : []),
    ];

    if (changes === 1) {
      const link = await connectionFor(candidate.second, ready, gate, connections, { walked: Boolean(walk) });
      if (!link) continue;
      return [...head, optionLeg(candidate.second, link.departure, link.pattern, link.segment, ready)];
    }

    const tail = await tailFrom(candidate.second, ready, {
      gate, destination, changes, connections, spread, walked: Boolean(walk),
    });
    if (tail) return [...head, ...tail];
  }
  return null;
}

/**
 * The rest of a journey that still needs more than one train.
 *
 * The planned leg is used for the BOARD — which station, which routes — and
 * then dropped: where that train actually puts you down, and whether it
 * finishes the job on its own, comes off its pattern through the same
 * `shapeOn` the first train went through. That is what gets Carnegie to
 * Merlynston home. The graph proposes a hop across town to some Upfield
 * platform, and the train that turns up is a Werribee service whose pattern
 * says Southern Cross, North Melbourne and Footscray — so the change is made
 * at whichever of those has an Upfield train, not at the one the stop list
 * liked.
 */
async function tailFrom(leg, readyAt, { gate, destination, changes, connections, spread, walked = false }) {
  const c = corridorById(leg.corridorId);

  for await (const link of connectionsFor(leg, readyAt, gate, connections, leg.toStopId, { walked })) {
    const shape = shapeOn(link.pattern, {
      corridor: c, from: leg.fromStopId, destination, points: spread, avoid: gate.avoid,
    });
    if (!shape || Date.parse(shape.board.departAt) < link.earliest) continue;
    const legs = await legsFrom(
      { ...shape, departure: link.departure, pattern: link.pattern },
      { gate, destination, changes: changes - 1, connections, spread, readyAt },
    );
    if (legs) return legs;
  }
  return null;
}

/** The walk leg with the two clock times a screen draws it with. */
function timedWalk(walk, arriveAt) {
  return {
    ...walk,
    departAt: arriveAt,
    arriveAt: new Date(Date.parse(arriveAt) + walk.minutes * 60_000).toISOString(),
  };
}

/** The leg from one stop to another, on this corridor if it carries both. */
function legTo(c, fromStopId, toStopId) {
  const a = c?.index?.get(Number(fromStopId));
  const b = c?.index?.get(Number(toStopId));
  if (a !== undefined && b !== undefined) {
    const leg = legOn(c, a, b);
    if (leg) return leg;
  }
  return directLegs(corridorsThrough(fromStopId), Number(fromStopId), Number(toStopId))[0] ?? null;
}

/**
 * Where to get off THIS run to finish the journey on one more train.
 *
 * Every stop the run actually makes, scored on what is left afterwards: the
 * fewest hops to the destination wins, and a tie goes to the earlier stop
 * because a change made sooner is a change made with the train still behind
 * you. Costs nothing — the pattern is already in hand — and it is the only
 * interchange choice in this file that cannot be wrong about where a train
 * stops.
 */
export function changesOnPattern(c, fromStopId, pattern, toStopId, { max = CHANGE_POINTS } = {}) {
  const stops = pattern?.stops ?? [];
  const board = stops.findIndex((s) => Number(s.stopId) === Number(fromStopId));
  if (board < 0) return [];

  // On a stitched pattern the change is looked for on the FIRST run before
  // the second: a Carnegie train that carries on as a Sunbury service could
  // "change" at Footscray for a Werribee train back to Flinders Street, and
  // by the hop count that even looks short — but Malvern, on the way in, is
  // the change a person would make. Only if no stop before the junction
  // works does the search go on past it.
  const junction = pattern?.stitched
    ? stops.findIndex((s) => Number(s.stopId) === Number(pattern.stitched.at))
    : -1;

  // Every viable change, ranked. A ranked LIST rather than one winner,
  // because the graph's favourite can be right on paper and empty on the
  // night: after eight the Upfield trains run the loop the other way and the
  // one Flinders Street departure in two hours is gone before the walk from
  // Town Hall is done, while Melbourne Central has two — so the walk from
  // State Library is tried when the first comes back with no connection.
  const search = (limit) => {
    const found = new Map();
    for (let i = board + 1; i < limit; i += 1) {
      const stopId = Number(stops[i].stopId);
      if (stopId === Number(toStopId) || stopId === Number(fromStopId)) continue;
      for (const onto of boardingPoints(stopId)) {
        if (onto.stopId === Number(toStopId) || onto.stopId === Number(fromStopId)) continue;
        const [second] = directLegs(corridorsThrough(onto.stopId), onto.stopId, Number(toStopId));
        if (!second) continue;
        const cost = legCost(second) + (onto.walk ? 1 : 0);
        const first = legTo(c, fromStopId, stopId);
        if (!first) continue;
        const key = onto.stopId;
        const have = found.get(key);
        if (have && have.cost <= cost) continue;
        found.set(key, { cost, order: i, walk: onto.walk ? walkLegBetween(stopId, onto.stopId) : null, first, second });
      }
    }
    return [...found.values()]
      .sort((a, b) => a.cost - b.cost || Number(Boolean(a.walk)) - Number(Boolean(b.walk)) || a.order - b.order);
  };
  // Before the junction first, then the rest — but the rest stays on the
  // list as alternatives, because the first choice can have no train.
  const before = junction > board ? search(junction + 1) : [];
  const seen = new Set(before.map((x) => x.second.fromStopId));
  const after = search(stops.length).filter((x) => !seen.has(x.second.fromStopId));
  return [...before, ...after].slice(0, max);
}

/** The single best change, for callers and tests that want one. */
export function changeOnPattern(c, fromStopId, pattern, toStopId) {
  return changesOnPattern(c, fromStopId, pattern, toStopId, { max: 1 })[0] ?? null;
}


/**
 * Five candidates in town, three everywhere else.
 *
 * A city platform is the one place the direction filter cannot help: at
 * Flinders Street every train is outbound and half of them are outbound the
 * wrong way. Two extra rows is two extra calls in the worst case and the
 * difference between an answer and an empty card.
 */
export function candidateCount(c, leg) {
  // THREE ON A V/LINE LEG, WHEREVER IT IS BOARDED — Southern Cross included,
  // and that is the point: it is the biggest board in the network and a
  // regional leg off it is still an hourly train. See REGIONAL_CANDIDATES.
  if (isRegionalLeg(leg)) return REGIONAL_CANDIDATES;
  const at = c?.index?.get(Number(leg.fromStopId));
  const [start, end] = c ? cityBlock(c) : [0, -1];
  const inCity = at !== undefined && at >= start && at <= end;
  return inCity ? CITY_CANDIDATES : CANDIDATES;
}

/**
 * The first train out of the change station that can actually be caught.
 *
 * The station's change time of buffer — see `changeMinutes` — measured against
 * the PATTERN'S departure rather than the board's row, because the row is the
 * time at that platform and the pattern is the time this journey uses, and if
 * they disagree the pattern is the one the arrival was computed from.
 *
 * There is no "tight but possible" tier and there must not be one. A train
 * that leaves inside the change time is not offered at all: the whole of this
 * work is that a plan should be a thing a person can actually do, and a
 * two-minute change at Footscray with an amber warning on it is still a
 * two-minute change at Footscray.
 */
async function connectionFor(leg, arriveAt, gate, limit = CONNECTIONS, { walked = false } = {}) {
  for await (const link of connectionsFor(leg, arriveAt, gate, limit, null, { walked, mustReach: true })) {
    // THE SAME TRAIN, UNDER ITS NEXT NAME. The first train of a plan is already
    // followed across the tunnel's two-run modelling; a connection was not, and
    // that is the whole difference between Lara to Carnegie being one change
    // and two. The Sunbury train out of Footscray ends its run at State Library
    // and carries on as a Pakenham service to Carnegie — one train, no second
    // change, and PTV models it as two runs. One extra call, spent only when
    // this run does not reach the destination on its own.
    let pattern = link.pattern;
    if (!segment(pattern, leg.fromStopId, leg.toStopId, gate.avoid) && pattern?.continues?.runRef) {
      const next = await gate.run(pattern.continues.runRef, pattern.continues.routeId ?? pattern.routeId);
      if (next) pattern = stitchRuns(pattern, next);
    }
    const seg = segment(pattern, leg.fromStopId, leg.toStopId, gate.avoid);
    if (!seg || Date.parse(seg.departAt) < link.earliest) continue;
    return { departure: link.departure, pattern, segment: seg };
  }
  return null;
}

/**
 * The trains out of the change station that could be caught, with patterns.
 *
 * The same board and the same change time, stopping one step earlier: `connectionFor`
 * wants the leg the graph asked for and a journey with two changes in it wants
 * the TRAIN, because what it does after the change station is the thing that
 * decides the rest of the journey. `earliest` rides along so that whoever reads
 * the pattern can hold its departure to the same minute the board row was held
 * to.
 *
 * ONE PATTERN AT A TIME, and that is the whole difference between a plan that
 * finishes and a plan that spends its minute. Both callers take the FIRST
 * connection that works and stop; fetching the other two in parallel was three
 * signed calls for an answer the first one had. It read as free because the
 * three went out together, and it was not free: the gate hands this app one
 * call every three and three quarter seconds, so three at once is three waits
 * in a row with two of them thrown away. Asking lazily costs exactly as long
 * when the first row is the answer and exactly the same when it is not.
 */
/**
 * The routes asked for at a change station: the leg's own, without V/Line when
 * the leg is a metropolitan one.
 */
function connectionRoutes(leg) {
  const ids = Array.isArray(leg.routeIds) ? leg.routeIds : [];
  const metro = ids.filter((id) => !lineRecord(Number(id))?.regional);
  return isRegionalLeg(leg) || !metro.length ? ids : metro;
}

/**
 * Whether this row stops running before it reaches where the leg is going.
 *
 * The bundle knows the order of a line and the row names where the run ENDS,
 * so a Belgrave-line train signed for Camberwell is known not to reach Union
 * without a call. It matters most at night, when a change station's board is
 * mostly short workings: Richmond's first two Belgrave-line trains after ten
 * were both Camberwell terminators, and the plan read both patterns — seven
 * seconds — to learn what the row already said.
 *
 * DELIBERATELY NARROW, because a false yes costs somebody a connection. It
 * says yes only when the row's own line lists the boarding station, the
 * destination and the terminus, the terminus lies strictly between the first
 * two, and none of the three is in the city — where the loop and the tunnel are
 * one ordered list that cannot say which way round a train goes. A row it
 * cannot place is a row it does not judge.
 */
function endsShort(departure, leg) {
  const stops = lineStops(Number(departure?.routeId));
  const at = (stopId) => stops.findIndex((x) => Number(x.stopId) === Number(stopId));
  const from = at(leg.fromStopId);
  const to = at(leg.toStopId);
  const end = at(departure?.finalStopId);
  if (from < 0 || to < 0 || end < 0 || from === to) return false;
  if ([from, to, end].some((i) => isCityEnd(stops[i]))) return false;
  return to > from ? end > from && end < to : end < from && end > to;
}

async function* connectionsFor(leg, arriveAt, gate, limit = CONNECTIONS, reaching = null, { walked = false, mustReach = false } = {}) {
  const station = stationById(leg.fromStopId);
  const c = corridorById(leg.corridorId);
  // The station's own minimum, floored for a V/Line train and topped up by
  // whatever margin this person asked for. `arriveAt` is the time the previous
  // train really gets in — its delay already carried onto it by `carryDelay` —
  // so a late train shortens the connection exactly as it does on the ground,
  // and the train that can no longer be caught drops off this list.
  const earliest = Date.parse(arriveAt) + changeMinutes(station, leg, { walked }) * 60_000;

  // ── A DEEPER BOARD IS ASKED FOR ON EVIDENCE, NOT ON A GUESS ────────────────
  //
  // PTV answers eight rows per route and direction, which at Footscray is
  // eighty minutes of tunnel trains — and the Geelong train from Lara gets
  // there in ninety-two. That connection is real and is on no ordinary board,
  // so a deeper one has to be asked for. What it must not be asked for is a
  // change the board in hand can already see: the rule used to be a clock —
  // half an hour, then any change past it — and a clock cannot tell a Geelong
  // train from the third Frankston train of the morning. Carnegie to Southern
  // Cross paid for a second, deeper copy of a board whose first copy had the
  // train on it.
  //
  // The board itself settles it for nothing. If not one row on it is at or
  // after the minute this change can be made, the board does not reach the
  // change and a deeper one is the only thing that can; if one is, the answer
  // is already in hand.
  // A metro hop that also lists V/Line (Flinders Street to Southern Cross
  // names Werribee AND Geelong) used to buy both halves of the interchange
  // board. The V/Line token is the one that waited eight seconds and left
  // Carnegie to Southern Cross with nothing to show. Ask only metropolitan
  // when the leg is not itself a V/Line leg; the filter still runs after.
  const routes = connectionRoutes(leg);
  let rows = await gate.departures(leg.fromStopId, routes);
  if (!rows) return;
  // A BOARD WITH NOTHING ON IT HAS NO DEEPER VERSION. `max_results` is per route
  // and direction and the window is the server's, so a deeper board is the same
  // rows and more of them: none and more of them is none. After nine at night
  // the Belgrave and Lilydale lines leave Flinders Street and Melbourne Central
  // for no one, and the plan paid for each of those boards twice, four slow
  // calls, then ran out its deadline before it could try a route that works.
  if (rows.length && !rows.some((d) => Date.parse(d.estimatedAt ?? d.scheduledAt ?? '') >= earliest)) {
    const deeper = await gate.departures(leg.fromStopId, routes, DEEP_BOARD_ROWS);
    if (deeper?.length) rows = deeper;
  }

  const candidates = rows
    .filter((d) => d?.runRef
      // Stated here as well as inside `matchesLeg`. A connection is the leg a
      // person is told to wait on a platform for, which is the worst place in
      // the app to be offered a train that is not coming.
      && !d.cancelled
      && matchesLeg(d, leg, station, c, gate.avoid)
      && !(mustReach && endsShort(d, leg))
      && (reaching === null || routeCalls(d, reaching))
      && Date.parse(d.estimatedAt ?? d.scheduledAt ?? '') >= earliest)
    .slice(0, limit);

  for (const departure of candidates) {
    const pattern = await gate.run(departure.runRef, departure.routeId);
    // A null pattern is the budget, the deadline or a busy gate, and all three
    // mean the same thing: there is nothing left to spend on the rows behind
    // this one. Anything else that comes back empty is just this train having
    // no pattern, and the next row still deserves its turn.
    if (!pattern) {
      if (gate.spent()) return;
      continue;
    }
    yield { departure, pattern, earliest };
  }
}

/**
 * Whether a train on this line could call at that station at all.
 *
 * A second call saver, and only the two-change fallback uses it. At Flinders
 * Street at half past nine the lines that reach North Melbourne and the lines
 * that reach Sandringham are both on the board and both signed outbound, and
 * with two connections to spend on the whole crossing, two Sandringham trains
 * is the difference between a journey and an empty screen. The line's own stop
 * list settles it for nothing, and its through-running half counts too — a
 * train signed for one end of the city carries on as the other.
 *
 * Wrong in the safe direction, as every pre-filter in this file is: a train it
 * lets through is still gated on the pattern, and a corridor it is unsure
 * about it does not narrow.
 */
function routeCalls(departure, stopId) {
  const at = Number(stopId);
  return [Number(departure?.routeId), Number(departure?.throughRouteId)]
    .filter(Number.isFinite)
    .some((routeId) => lineStops(routeId).some((s) => s.stopId === at));
}

function optionLeg(leg, departure, pattern, seg, previousArriveAt = null) {
  const wait = previousArriveAt
    ? Math.round((Date.parse(seg.departAt) - Date.parse(previousArriveAt)) / 60_000)
    : null;
  return {
    ...leg,
    // The RUN'S route, not the leg's: a through-running service is one train
    // across two route ids, and the card is coloured by what actually turns up
    // so that it agrees with the departure board.
    routeId: Number(pattern?.routeId) || Number(departure?.routeId) || leg.routeId,
    routeName: departure?.routeName ?? '',
    destination: departure?.destination ?? '',
    departAt: seg.departAt,
    arriveAt: seg.arriveAt,
    platform: seg.platform ?? departure?.platform ?? null,
    delayMinutes: departure?.delayMinutes ?? null,
    runRef: departure?.runRef ?? null,
    stops: seg.stops,
    // Where this train becomes another run of itself, if that happens inside
    // the leg — the journey screen marks it, the card lists both lines, and
    // nobody is told to change.
    continues: pattern?.stitched && seg.stops.some((st) => Number(st.stopId) === Number(pattern.stitched.at))
      && Number(seg.stops[seg.stops.length - 1].stopId) !== Number(pattern.stitched.at)
      ? pattern.stitched
      : null,
    ...(wait === null ? {} : { waitMinutes: wait }),
  };
}

function optionOf(legs, now) {
  const leave = legs[0].departAt;
  const arrive = legs[legs.length - 1].arriveAt;
  return {
    leave,
    arrive,
    minutes: Math.round((Date.parse(arrive) - Date.parse(leave)) / 60_000),
    // Trains, not legs: the subway between State Library and Melbourne Central
    // is part of one change, and counting it as a second would rank a journey
    // below one that is genuinely harder.
    changes: Math.max(0, trainLegs(legs).length - 1),
    plannedAt: new Date(now).toISOString(),
    legs,
  };
}

/**
 * The counted, memoised, budgeted `io`.
 *
 * Departures are remembered per station and route set for the life of the plan,
 * because three itineraries out of Carnegie are one board; runs are remembered
 * per ref for the same reason. The counter increments BEFORE the await so that
 * three patterns fetched in parallel cost three against the budget at the
 * moment they are asked for, not at the moment they come back.
 */
function callGate(io, budget, now = Date.now(), avoid = null, deadlineAt = null, enoughAt = null, stopped = null) {
  const boards = new Map();
  // Boards that ANSWERED with no trains at all, as opposed to boards that did
  // not answer (busy, past the deadline), which look the same once the rows are
  // mapped to a list. See `isEmpty`.
  const emptied = new Set();
  let soft = Infinity;
  const runs = new Map();
  const avoiding = normaliseAvoid(avoid);
  let calls = 0;
  let busy = false;
  let expired = false;

  // ── AND A REFUSAL BY THE CLOCK, WHICH IS THE SAME ANSWER ───────────────────
  //
  // Nothing above bounds a plan in TIME. It bounds it in calls, and the two
  // stopped being the same thing the moment the server started queueing: the
  // gate hands this app one call every three and three quarter seconds, a
  // patient one waits eight for a token and a busy one retries after five, so
  // a plan of fifteen calls is fifty-seven seconds of a person watching FINDING
  // TRAINS with nothing wrong anywhere. Measured on the deployed service, not
  // supposed. There is no length of loader that is an answer, so there is now a
  // deadline: past it every call resolves `null` — the same `null` a spent
  // budget has always handed back, which every caller here already unwinds
  // through — and the plan returns whatever it managed to build.
  //
  // IT IS THE REAL CLOCK, not `now`. `now` is the minute the plan is about, is
  // injected, and in a test is a fixed point in September; this is how long
  // somebody has been waiting.
  // `let`, because a board's plan moves it once it has something on screen —
  // see `lingerMs` in `time`. It only ever moves later.
  let deadline = Number.isFinite(deadlineAt) ? deadlineAt : null;
  // A caller that no longer wants the answer — a board somebody has left, or
  // one re-read under a newer plan — is a refusal like the budget's: the next
  // call is not made, and the plan unwinds with what it has.
  const halted = () => typeof stopped === 'function' && Boolean(stopped());
  const overdue = () => halted() || (deadline !== null && Date.now() >= deadline);
  const enough = Number.isFinite(enoughAt) ? enoughAt : null;

  // The race that makes an in-flight call obey it too. Without this the
  // deadline only stops NEW calls, and the one already out can take
  // twenty-eight seconds on its own: ten for the fetch, five for the retry a
  // busy answer is owed, ten for the second attempt. The fetch is left to
  // finish into its own cache — cancelling it would throw away a pattern the
  // next plan would pay for again — but nobody waits on it any more.
  const byDeadline = (pending) => {
    if (deadline === null) return pending;
    return new Promise((resolve, reject) => {
      let timer = null;
      // Re-armed rather than fired when the deadline has moved since this call
      // went out: a pattern asked for at nineteen seconds, on a plan whose
      // first option at eight seconds bought it a longer deadline, is still
      // wanted at twenty.
      const arm = () => {
        timer = setTimeout(() => {
          if (Date.now() < deadline) { arm(); return; }
          timer = null;
          expired = true;
          resolve(null);
        }, Math.max(0, deadline - Date.now()));
      };
      arm();
      pending.then(
        (value) => { if (timer) { clearTimeout(timer); resolve(value); } },
        (error) => { if (timer) { clearTimeout(timer); reject(error); } },
      );
    });
  };

  // ── A BUDGET REFUSAL ENDS THE SPENDING, NOT THE PLAN ───────────────────────
  //
  // The server rations this app's calls, and a refusal used to reject, which
  // rejected the whole plan: nine patterns in hand, four options half built,
  // and the screen said "Couldn't reach PTV" because the tenth call was one
  // over the minute. That is the worst possible trade. Three good options are
  // better than an error, and the person asking is not owed every train that
  // exists, only the next few that work.
  //
  // So a busy answer marks the plan and hands back `null`, which every caller
  // here already handles. `busy` is carried out so the screen can say "there
  // may be more in a few seconds" rather than pretending this is the whole
  // timetable. Spending itself stops in `time` once there is an option to
  // show — pushing `calls` to the budget here used to kill a plan that had
  // only a board and a first pattern, with the change-station 503 still the
  // only thing standing between the person and a train.
  //
  // ANY OTHER FAILURE STILL REJECTS. A dead upstream is not a budget decision
  // and must not read as a short plan.
  const settle = (pending) => pending.catch((e) => {
    if (!isBusy(e)) throw e;
    busy = true;
    return null;
  });

  return {
    get calls() { return calls; },
    get busy() { return busy; },
    get now() { return now; },
    // The avoid set, carried rather than held in the module variable: every
    // reader of it here is on the far side of an await. See the note on
    // `AVOID`.
    get avoid() { return avoiding; },
    /** True when the plan has run out of calls, of patience, or of both. */
    get expired() { return expired; },
    /**
     * Whether a plan that already has an answer should stop looking for more.
     *
     * Asked only where an option has just been built, and answered on the real
     * clock rather than on the count of calls: the question is how long
     * somebody has been watching the loader, and two calls through a busy gate
     * take longer than six through an idle one.
     */
    hurry: () => enough !== null && Date.now() >= enough,
    /** Move the deadline later, never earlier. A no-op on a plan with none. */
    extend(at) {
      if (deadline !== null && Number.isFinite(at) && at > deadline) deadline = at;
    },
    /**
     * A ceiling on calls below the budget, for one stage of a plan.
     *
     * A refused call is not cached, so what a capped stage did not get to is
     * asked for, and paid for, when a later stage lifts the cap.
     */
    cap(limit) {
      soft = Number.isFinite(limit) ? limit : Infinity;
    },
    /** More calls than the plan started with, for a search that has earned them. */
    allow(more) {
      if (calls < budget && Number.isFinite(more) && more > 0) budget += more;
    },
    spent: () => calls >= Math.min(budget, soft) || expired || overdue(),

    departures(stopId, routeIds, max = null) {
      const platform = `${Number(stopId)}:${[...new Set(routeIds)].sort((a, b) => a - b).join(',')}`;
      const rows = Number(max) || 0;
      const key = `${platform}:${rows}`;
      if (boards.has(key)) return boards.get(key);
      // A DEEPER BOARD IS A SUPERSET, so a shallow ask behind one is free.
      // PTV's `max_results` is per route and direction, which makes the deep
      // board every row the ordinary one has and then some — and the ordinary
      // ask arriving second is a second signed call for rows already in hand.
      const deeper = [...boards.keys()].find((k) => {
        const cut = k.lastIndexOf(':');
        return k.slice(0, cut) === platform && depth(Number(k.slice(cut + 1))) > depth(rows);
      });
      if (deeper) return boards.get(deeper);
      if (calls >= Math.min(budget, soft) || expired || overdue()) return Promise.resolve(null);
      calls += 1;
      const pending = byDeadline(settle(Promise.resolve(io.departures(stopId, routeIds, max ? { max } : undefined))))
        // ONE ROW PER TRAIN, whatever the `io` hands over. PTV answers a
        // departures call per ROUTE, so the 16:07 to Waurn Ponds is on the
        // board twice — once as a Geelong run and once as a Warrnambool one,
        // with DIFFERENT run refs, which means neither the ref nor a Set can
        // tell they are one train. Two copies is two pattern calls out of a
        // budget of twenty-eight, two identical options on the card, and a
        // dedupe at the end that cannot see they are the same journey.
        // `fetchDepartures` already collapses them and the collapse is
        // idempotent; doing it here as well means a planner given a raw board
        // — a test, a future caller — cannot be made to pay twice.
        .then((rows) => {
          if (Array.isArray(rows) && !rows.length) emptied.add(platform);
          return Array.isArray(rows) ? oneRowPerTrain(rows) : [];
        });
      boards.set(key, pending);
      return pending;
    },

    /**
     * Whether this station's board for these routes has come back, and had
     * nothing on it.
     *
     * Not "is small": a board that was refused, or that the deadline cut off, is
     * not one that showed no trains, and calling it empty would write off a
     * change station because the gate was busy. Only an answer counts.
     */
    isEmpty(stopId, routeIds) {
      return emptied.has(`${Number(stopId)}:${[...new Set(routeIds)].sort((a, b) => a - b).join(',')}`);
    },

    // `routeId` says which route_type PTV will discuss this ref under, and it
    // is not optional for V/Line: run ref 8828 is a 22-stop metropolitan
    // pattern under route_type 0 and the 4-stop Ballarat run under 3 — two
    // real trains, one ref. It is in the cache key for exactly that reason.
    run(runRef, routeId = null) {
      const key = `${Number(routeId) || 0}:${String(runRef)}`;
      if (runs.has(key)) return runs.get(key);
      if (calls >= Math.min(budget, soft) || expired || overdue()) return Promise.resolve(null);
      calls += 1;
      const pending = byDeadline(settle(Promise.resolve(io.run(runRef, routeId))));
      runs.set(key, pending);
      return pending;
    },
  };
}

/** The same two trains proposed twice is one option. */
function dedupe(options) {
  const seen = new Set();
  return options.filter((o) => {
    const key = o.legs.map((l) => l.runRef ?? `walk:${l.fromStopId}`).join('>');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Best first: arrive soonest, then fewest changes, then leave latest.
 *
 * Leave LATEST is the one that looks backwards and is not. Two trains arriving
 * at the same minute are the same journey as far as the day is concerned, and
 * the one that leaves later is the one that does not need you on the platform
 * for eleven minutes first.
 */
export function rank(options) {
  return [...options].sort((a, b) => (
    Date.parse(a.arrive) - Date.parse(b.arrive)
    || a.changes - b.changes
    || Date.parse(b.leave) - Date.parse(a.leave)
  ));
}

/**
 * The line's current state against each leg, for the card to say out loud.
 *
 * Attached rather than looked up on the screen because the option is stashed
 * and re-read on the journey page, and a leg that knows its own status cannot
 * be drawn next to somebody else's. `statusFor` is `app-state`'s, injected —
 * this module does not import the store.
 */
export function annotate(options, statusFor) {
  if (typeof statusFor !== 'function') return options;
  return options.map((o) => ({
    ...o,
    // ANY leg that is not a train. A walk has no line and never did; a
    // replacement bus has a route id that names the line it is standing in for,
    // and stamping that line's status on it would put SUSPENDED on the only leg
    // of the journey that is actually running.
    legs: o.legs.map((leg) => (leg.kind != null && leg.kind !== 'train'
      ? leg
      : { ...leg, status: statusFor(leg.routeId) ?? null })),
  }));
}

/**
 * One train from the board, as a journey: the direct option for a row that
 * already goes there.
 *
 * The departures board answers "does this train stop at mine" for free, off
 * the bundle, and a direct row used to open the train card. With a destination
 * set it opens the JOURNEY instead — the same page a planned change opens, so
 * getting off, the platform and Follow are in one place whichever kind of row
 * was tapped. This builds that page's option from the row's own run, exactly
 * as the planner would have built a direct leg: the pattern, followed onto its
 * next run where the tunnel splits the train in two, and cut at both ends by
 * `segment`, which is also where AVOID is enforced against the real train.
 *
 * ONE CALL, TWO WHERE THE TRAIN BECOMES ANOTHER RUN, both through `io.run` and
 * both usually already in `fetchRun`'s cache because the board just read them.
 * Null — no pattern, the train does not reach there, or it is carried through
 * something the person asked to avoid — and the caller opens the train card as
 * it always has.
 */
export async function directOption(departure, fromStopId, toStopId, {
  io, now = Date.now(), avoid = null, statusFor = null,
} = {}) {
  if (!departure?.runRef || departure.cancelled || typeof io?.run !== 'function') return null;
  const avoiding = normaliseAvoid(avoid);
  const from = Number(fromStopId);
  const to = Number(toStopId);
  if (!(from > 0) || !(to > 0) || from === to) return null;
  if (vlineBarred(departure.routeId, from, to)) return null;

  let pattern = await io.run(departure.runRef, departure.routeId);
  if (!pattern) return null;
  if (!segment(pattern, from, to, avoiding) && pattern.continues?.runRef) {
    const next = await io.run(pattern.continues.runRef, pattern.continues.routeId ?? pattern.routeId);
    if (next) pattern = stitchRuns(pattern, next);
  }
  const seg = segment(pattern, from, to, avoiding);
  if (!seg) return null;

  const routeId = Number(pattern?.routeId) || Number(departure?.routeId) || null;
  const leg = {
    corridorId: null,
    routeId,
    routeIds: [routeId].filter(Boolean),
    fromStopId: from,
    toStopId: to,
    direction: null,
    hops: Math.max(1, seg.stops.length - 1),
    via: seg.stops.slice(1, -1).map((st) => stationById(st.stopId)).filter(Boolean),
  };
  return annotate([optionOf([optionLeg(leg, departure, pattern, seg)], now)], statusFor)[0];
}

/**
 * One train that ALREADY LEFT, as a journey.
 *
 * `directOption` is for a row that reaches the destination on its own; this is
 * for the trains that do not: the departed band asks it about the last few
 * trains out of a station, because the person standing on one of them is the
 * person who needs to be told where to change. The planner cannot find these
 * itself — it starts from the board, and the board only lists what is still to
 * come.
 *
 * It is the same work `timeItinerary` does for one candidate — the run's own
 * pattern decides where it can put you down, then a connection is read at that
 * station — aimed at ONE named train instead of at the next few.
 *
 * THE CHANGE HAS TO BE STILL AHEAD. A train that left twenty minutes ago has
 * passed some of the places it could have been changed at, and a journey that
 * says "change at Caulfield" to somebody already beyond Caulfield is worse than
 * none. Shapes whose change is behind `now` are dropped, and the next stop on
 * the run that can still reach the destination takes their place.
 *
 * Null when the train does not get there, has no pattern, or nothing can be
 * connected in the calls it is allowed. A failing `io` rejects, as everywhere.
 */
export async function optionFromRun(departure, fromStopId, toStopId, {
  io, now = Date.now(), avoid = null, statusFor = null, budget = 8, deadlineMs = PLAN_DEADLINE_MS,
} = {}) {
  if (!departure?.runRef || departure.cancelled || typeof io?.run !== 'function') return null;
  const from = Number(fromStopId);
  const to = Number(toStopId);
  if (!(from > 0) || !(to > 0) || from === to) return null;
  if (vlineBarred(departure.routeId, from, to)) return null;

  const routeId = Number(departure.routeId);
  const corridor = corridorsThrough(from).find((c) => c.routeIds.includes(routeId)) ?? null;
  if (!corridor) return null;

  const gate = callGate(io, budget, now, avoid, Number.isFinite(deadlineMs) ? Date.now() + deadlineMs : null);
  let pattern = await gate.run(departure.runRef, departure.routeId);
  if (!pattern) return null;
  if (!segment(pattern, from, to, gate.avoid) && pattern.continues?.runRef) {
    const next = await gate.run(pattern.continues.runRef, pattern.continues.routeId ?? pattern.routeId);
    if (next) pattern = stitchRuns(pattern, next);
  }

  const shape = shapeOn(pattern, { corridor, from, destination: to, avoid: gate.avoid });
  if (!shape) return null;
  const ahead = (sh) => Date.parse(sh.board?.arriveAt ?? '') >= now;
  const usable = [shape, ...(shape.alternatives ?? [])].filter(ahead);
  if (!usable.length) return null;

  const option = await optionFrom(
    { ...usable[0], alternatives: usable.slice(1), departure, pattern },
    { now, gate, destination: to, changes: 1, connections: CONNECTIONS, spread: CHANGE_POINTS },
  );
  return option ? annotate([option], statusFor)[0] : null;
}

/**
 * The whole trip, and the one call the screen makes.
 *
 * `{ ok: true, options, calls, plannedAt }` — with an EMPTY options array when
 * nothing runs in the next two hours, which is an answer and not a failure —
 * or `{ ok: false, reason }` where the reason is 'same', 'unknown' or
 * 'no-route'. A dead `io` rejects with its own message, because "couldn't reach
 * PTV" is a different sentence from "no trains".
 */
export async function planTrip(fromStopId, toStopId, {
  io, now = Date.now(), statusFor = null, budget = CALL_BUDGET, max = PLAN_OPTIONS, avoid = null,
  deadlineMs = PLAN_DEADLINE_MS, enoughMs = PLAN_ENOUGH_MS,
  onePerTrain = false, lingerMs = null, onOption = null, stopped = null, lastResort = false,
} = {}) {
  const plannedAt = new Date(now).toISOString();
  const planned = plan(fromStopId, toStopId, { avoid });
  if (!planned.ok) {
    return {
      ok: false, reason: planned.reason, options: [], calls: 0, plannedAt, wrongWay: [], noOption: [],
    };
  }

  // `onOption` hears the options so far, ranked and annotated exactly as the
  // final answer will be, each time one more is found. A board draws the
  // first train's journey the moment it exists and the rest as they land.
  const progress = typeof onOption === 'function'
    ? (options) => onOption(annotate(options, statusFor))
    : null;
  const {
    options, calls, busy, slow, wrongWay, noOption,
  } = await time(planned.itineraries, {
    now, io, budget, max, avoid, deadlineMs, enoughMs, onePerTrain, lingerMs, onOption: progress, stopped, lastResort,
  });
  // `busy` says the gate cut this plan short. The options in hand are real and
  // are shown; what the screen adds is that there may be more of them in a few
  // seconds, which is true and is not the same sentence as "no trains".
  // `slow` says the deadline stopped it rather than the budget. The two read
  // almost the same on the glass and are not the same fact: busy is this app
  // rationing itself and is over in seconds, slow is the plan having waited as
  // long as anybody should be asked to.
  return {
    ok: true, reason: null, options: annotate(options, statusFor), calls, busy, slow, plannedAt, wrongWay, noOption,
  };
}
